import { RegistrationService } from '@app/registration/registration.service';
import { NgxMaskModule } from 'ngx-mask';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { HttpService } from '@app/@core/services/http.service';
import { QUEUE_CONSTANTS } from '@app/shared/constants/queue.constants';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { CommonService } from '@app/shared/services/common.service';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { NgbDateStruct, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '@app/@core/services/alert.service';
import {
  ApplicationOpenFromGridService,
  ApplicationOpenGridRow,
  lookupUserReturnTargets,
  normalizeNumericApplicationId
} from '@app/shared/services/application-open-from-grid.service';
import { ModalConfig } from '@app/shared/constants/modal.model';
import { StaffDocumentUploadNoticeModalService } from '@app/shared/services/staff-document-upload-notice-modal.service';
import { getCancelFileUploadModalConfig } from '@app/shared/constants/cancel-file-upload-modal.util';
import { EducatorDuplicateDocumentGuardService } from '@app/shared/services/educator-duplicate-document-guard.service';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import _ from 'lodash';
import * as FileSaver from 'file-saver';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { educatorDocumentUploadWithClientBlobPut } from '../../educator-blob-upload.util';
import {
  appendProfileUploadToDashboardRow,
  buildProfileUploadRowForLocalCache,
  toDuplicateLookupOptions
} from '../../educator-duplicate-document.util';
import { LocalService } from '@app/@core/services/local.service';
import { ManageProfileService } from '@app/pages/manage-profile/manage-profile.service';
import {
  canShowDeleteEducatorDocument,
  parseDocumentDeletionStaffHoursFromApi,
  resolveLoginRoleId
} from '../../educator-document-delete-eligibility.util';
import { EducatorDocumentPreviewModalService } from '../../educator-document-preview-modal.service';
import { resolveEducatorDocumentFileUrl } from '../../educator-document-download.util';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { flipLookupUploadDropdownAbove } from '../../lookup-upload-dropdown.util';
import { SupportingDocumentRuleConfigService } from '@app/pages/page-designer/supporting-document-rule-config/supporting-document-rule-config.service';
import { SupportingDocumentGridOption } from '@app/pages/page-designer/supporting-document-grid/supporting-document-grid-config.model';
import { LOOKUP_USER_SECTION_FORM_IDS } from '../../lookup-user-form-ids.constants';

@Component({
  selector: 'lookup-user-experience-tab',
  standalone: true,
  imports: [CommonModule, DataGridComponent, ReactiveFormsModule, MaterialUIModule, NgxMatSelectSearchModule, NgxMaskModule, DropdownModule],
  templateUrl: './lookup-user-experience-tab.component.html',
  styleUrls: ['./lookup-user-experience-tab.component.scss']
})
export class LookupUserExperienceTabComponent implements OnInit, OnChanges, OnDestroy {
  // Today string for date max binding
  private lastEmploymentRow: any = null;
  today = new Date().toISOString().slice(0, 10);

  // Custom validator for teaching experience dates
  static teachingExperienceDateValidator(form: FormGroup) {
    const from = form.get('from')?.value;
    const to = form.get('to')?.value;
    if (!from || !to) return null;

    const parseDateMMDDYYYY = (val: string): Date | null => {
      const parts = val.split('/');
      if (parts.length !== 3) return null;
      const [m, d, y] = parts.map(Number);
      return new Date(y, m - 1, d);
    };

    const fromDate = parseDateMMDDYYYY(from);
    const toDate = parseDateMMDDYYYY(to);
    if (!fromDate || !toDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    // No future dates
    if (fromDate.getTime() > today.getTime() || toDate.getTime() > today.getTime()) {
      return { futureDate: true };
    }
    // from cannot be after to
    if (fromDate.getTime() > toDate.getTime()) {
      return { fromAfterTo: true };
    }
    return null;
  }
  // Out-of-State License status mapping (case-insensitive)
  private outOfStateStatusMap: { [key: string]: string } = {
    'invalid': '1',
    'revoked': '2',
    'suspended': '3',
    'deceased': '4',
    'fp invalid': '5',
    'drop': '6',
    'denied': '7',
    'surrender': '8',
    'expired': '9',
    'omitted': '10',
    'deficient': '11',
    'cancelled': '12',
    'fp archive': '13',
    'extended': '14',
    'extend reversed': '15'
  };

  private getStatusKey(status: string): string {
    if (!status) return '';
    const normalized = status.trim().toLowerCase();
    // Try direct match
    if (this.outOfStateStatusMap[normalized]) return this.outOfStateStatusMap[normalized];
    // Try ignoring extra spaces
    const compact = normalized.replace(/\s+/g, ' ');
    return this.outOfStateStatusMap[compact] || '';
  }
  @Input() educatorDashboardRow: unknown;
  @Output() requestDashboardRefresh = new EventEmitter<void>();

  // Section toggle booleans
  showOutOfStateLicenseSection: boolean = false;
  showTeachingExperienceSection: boolean = false;
  showAssignmentExperienceSection: boolean = false;
  showVerifiableBusiness: boolean = false;
  showVerifiableTeachingExperience: boolean = false;
  showEmploymentSection: boolean = false;
  teachingExperienceDeleteRow: any = null;

  // Selected rows copied on edit (for retrieving ids etc.)
  selectedOutOfStateLicense: any = null;
  selectedTeachingExperience: any = null;
  selectedAssignmentExperience: any = null;
  selectedEmploymentRow: any = null;

  // Column chooser toggles
  showOutOfStateLicColumnChooser: boolean = false;
  showOutOfStateLicSupportColumnChooser: boolean = false;
  showTeachingExpColumnChooser: boolean = false;
  showTeachingExpSupportColumnChooser: boolean = false;
  showAssignmentExpColumnChooser: boolean = false;
  showEmploymentColumnChooser: boolean = false;
  showEmploymentSupportColumnChooser: boolean = false;
  showVerifiableTeachingExpColumnChooser: boolean = false;
  showVerifiableTeachingExpSupportColumnChooser: boolean = false;
  showBusinessIndustryColumnChooser: boolean = false;
  showBusinessIndustrySupportColumnChooser: boolean = false;
  showBusinessIndustryForm: boolean = false;
  isEditBusinessIndustry: boolean = false;
  selectedBusinessIndustry: any = null;
  businessIndustryDeleteRow: any = null;
  showAssignmentExperienceForm: boolean = false;
  showEmploymentForm: boolean = false;
  showVerifiableTeachingExperienceForm: boolean = false;
  statesList: { key: string; value: string }[] = [];
  filteredVerifiableTeachingStates: { key: string; value: string }[] = [];
  stateFilterCtrl: FormControl = new FormControl('');
  verifiableTeachingStatesList: { key: string; value: string }[] = [];
  filteredVerifiableTeachingExpStates: { key: string; value: string }[] = [];
  verifiableTeachingExpStateFilterCtrl: FormControl = new FormControl('');
  isVerifiableTeachingNevada: boolean = false;
  showVerifiableTeachingSchoolName: boolean = false;
  districtLookup: { key: string; value: string }[] = [];
  schoolLookup: { key: string; value: string }[] = [];
  filteredDistricts: { key: string; value: string }[] = [];
  filteredSchools: { key: string; value: string }[] = [];
  districtFilterCtrl: FormControl = new FormControl('');
  schoolFilterCtrl: FormControl = new FormControl('');
  verifiableTeachingStatusLookup: { key: string; value: string }[] = [];
  outOfStateLicenseDeleteRow: any = null;
  employmentDeleteRow: any = null;
  verifiableTeachingExperienceDeleteRow: any = null;


  private destroy$ = new Subject<void>();
  @ViewChild('outOfStateLicGrid') outOfStateLicGridRef!: DataGridComponent;
  @ViewChild('outOfStateLicSupportGridRef') outOfStateLicSupportGridComp!: DataGridComponent;
  @ViewChild('assignmentExpGrid') assignmentExpGridRef!: DataGridComponent;
  @ViewChild('verifiableTeachingExpGrid') verifiableTeachingExpGridRef!: DataGridComponent;
  @ViewChild('verifiableTeachingExpSupportGridRef') verifiableTeachingExpSupportGridComp!: DataGridComponent;
  @ViewChild('employmentGrid') employmentGridRef!: DataGridComponent;
  @ViewChild('employmentSupportGridRef') employmentSupportGridComp!: DataGridComponent;
  @ViewChild('businessIndustryGrid') businessIndustryGridRef!: DataGridComponent;
  @ViewChild('businessIndustrySupportGridRef') businessIndustrySupportGridComp!: DataGridComponent;
  @Input() personId: string | null = null;
  @ViewChild('uploadModel') uploadModel!: TemplateRef<any>;
  @ViewChild('outOfStateLicenseEditModal') outOfStateLicenseEditModal!: TemplateRef<any>;
  @ViewChild('teachingExperienceEditModal') teachingExperienceEditModal!: TemplateRef<any>;
  @ViewChild('employmentEditModal') employmentEditModal: TemplateRef<any>;
  @ViewChild('verifiableTeachingExpEditModal') verifiableTeachingExpEditModal!: TemplateRef<any>;
  @ViewChild('businessIndustryEditModal') businessIndustryEditModal!: TemplateRef<any>;


  // Headers for grids
  outOfStateLicHeaders: any;
  outOfStateLicSupportHeaders: any;
  teachingExperienceHeaders: any;
  teachingExperienceSupportHeaders: any;
  verifiableTeachingExpHeaders: any;
  verifiableTeachingExpSupportHeaders: any;
  businessIndustryHeaders: any;
  businessIndustrySupportHeaders: any;
  assignmentExperienceHeaders: any;
  employmentHeaders: any;
  employmentSupportHeaders: any;

  // All columns for column chooser
  allOutOfStateLicColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allOutOfStateLicSupportColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allTeachingExpColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allTeachingExpSupportColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allAssignmentExpColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allEmploymentColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allEmploymentSupportColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allVerifiableTeachingExpColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allVerifiableTeachingExpSupportColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allBusinessIndustryColumns: { key: string; label: string; disabled?: boolean }[] = [];
  allBusinessIndustrySupportColumns: { key: string; label: string; disabled?: boolean }[] = [];

  // Visible columns based on user selection
  visibleOutOfStateLicColumns: { [key: string]: boolean } = {};
  visibleOutOfStateLicSupportColumns: { [key: string]: boolean } = {};
  visibleTeachingExpColumns: { [key: string]: boolean } = {};
  visibleTeachingExpSupportColumns: { [key: string]: boolean } = {};
  visibleAssignmentExpColumns: { [key: string]: boolean } = {};
  visibleEmploymentColumns: { [key: string]: boolean } = {};
  visibleEmploymentSupportColumns: { [key: string]: boolean } = {};
  visibleVerifiableTeachingExpColumns: { [key: string]: boolean } = {};
  visibleVerifiableTeachingExpSupportColumns: { [key: string]: boolean } = {};
  visibleBusinessIndustryColumns: { [key: string]: boolean } = {};
  visibleBusinessIndustrySupportColumns: { [key: string]: boolean } = {};

  // Row data for grids
  outOfStateLicRows: Record<string, unknown>[] = [];
  teachingExperienceRows: Record<string, unknown>[] = [];
  assignmentExperienceRows: Record<string, unknown>[] = [];
  employmentRows: Record<string, unknown>[] = [];
  verifiableTeachingExpRows: Record<string, unknown>[] = [];
  businessIndustryRows: Record<string, unknown>[] = [];

  // Data grid configs
  outOfStateLicDataGrid: DataGridVO = new DataGridVO();
  outOfStateLicSupportGrid: DataGridVO = new DataGridVO();
  teachingExperienceDataGrid: DataGridVO = new DataGridVO();
  teachingExperienceSupportGrid: DataGridVO = new DataGridVO();
  assignmentExperienceDataGrid: DataGridVO = new DataGridVO();
  employmentDataGrid: DataGridVO = new DataGridVO();
  employmentSupportGrid: DataGridVO = new DataGridVO();
  verifiableTeachingExpDataGrid: DataGridVO = new DataGridVO();
  verifiableTeachingExpSupportGrid: DataGridVO = new DataGridVO();
  businessIndustryDataGrid: DataGridVO = new DataGridVO();
  businessIndustrySupportGrid: DataGridVO = new DataGridVO();
  showOutOfStateLicenseForm: boolean = false;
  showTeachingExperienceForm: boolean = false;
  // Edit mode flags
  isEditOutOfStateLicense: boolean = false;
  isEditTeachingExperience: boolean = false;
  isEditAssignmentExperience: boolean = false;
  isEditEmployment: boolean = false;
  isEditVerifiableTeachingExperience: boolean = false;
  selectedVerifiableTeachingExperience: any = null;
  teachingExperienceForm: FormGroup;
  assignmentExperienceForm: FormGroup;
  employmentForm: FormGroup;
  verifiableTeachingExperienceForm: FormGroup;
  businessIndustryForm: FormGroup;
  // MatDialog refs
  private dialogRef: any;

  uploadData: any = {
    file: null,
    fileName: '',
    type: ''
  };
  selectedUploadMeta: { fileName: string; contentType: string } = {
    fileName: '',
    contentType: ''
  };
  errorMessages: Record<string, string> = {};
  placeholderMessage: Record<string, string> = {};
  toastMessage: Record<string, string> = {};
  genericMessage: Record<string, string> = {};
  uploadError: string | null = null;
  saveEnable = true;
  currentUploadSection: 'out-of-state-lic_notes' | 'teaching-experience' | 'assignment-experience' | 'employment-support' | 'business-industry-support' | 'verifiable-teaching-experience' = 'out-of-state-lic_notes';
  private uploadModalRef: NgbModalRef | null = null;
  errorMessage: Record<string, string> = {};
  uploadDocMetaForm: FormGroup;
  docCategoryOptions: { id: number; key: string; value: string }[] = [];
  defaultDocCategoryOptions: { id: number; key: string; value: string }[] = [];
  allDocTypeOptions: { id: number; parentid: number | null; key: string; value: string }[] = [];
  uploadDocCategoryOptions: SupportingDocumentGridOption[] = [];
  uploadFilteredDocTypeOptions: SupportingDocumentGridOption[] = [];
  uploadCategoryLabel: string = '';

  /** BR0036 */
  documentDeletionStaffHours: number | null = null;

  constructor(
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private httpService: HttpService,
    private modalService: NgbModal,
    private staffDocNoticeModal: StaffDocumentUploadNoticeModalService,
    private duplicateDocGuard: EducatorDuplicateDocumentGuardService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private applicationOpenFromGrid: ApplicationOpenFromGridService,
    private registrationService: RegistrationService,
    private localService: LocalService,
    private manageProfileService: ManageProfileService,
    private documentPreviewModal: EducatorDocumentPreviewModalService,
    private supportingDocLookupService: SupportingDocumentRuleConfigService
  ) {
    this.outOfStateLicenseForm = this.fb.group({
      state: [null, Validators.required],
      license_number: ['', [Validators.required, Validators.minLength(3)]],
      license_status: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60), Validators.pattern("^[A-Za-z\\s.,\\-&'()/]{2,60}$")]]
    });

    this.teachingExperienceForm = this.fb.group({
      state: [null, Validators.required],
      from: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      to: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      total_years: [null, Validators.required]
    }, { validators: [LookupUserExperienceTabComponent.teachingExperienceDateValidator] });

    this.assignmentExperienceForm = this.fb.group({
      district: [''],
      school: [''],
      assignment: [''],
      school_year: [''],
      hours: ['']
    });

    this.employmentForm = this.fb.group({
      employer_name: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(60), Validators.pattern(/^[A-Za-z0-9\s.,\-&'()/]{2,60}$/)]],
      from: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      to: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      city: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(60), Validators.pattern(/^[A-Za-z\s.\-,']{2,60}$/)]],
      state: [null, Validators.required],
      zip_code: [null, [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]]
    }, { validators: [LookupUserExperienceTabComponent.teachingExperienceDateValidator] });

    this.verifiableTeachingExperienceForm = this.fb.group({
      state: [null, Validators.required],
      district: [null],
      school: [null],
      school_name: ['', [Validators.maxLength(60), Validators.pattern(/^[A-Za-z\s.,\-&()/]{2,60}$/)]],
      from: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      to: ['', [Validators.required, this.validateDate, this.validateDateNotBeforeDob]],
      total_years: [null, Validators.required],
      status: [null, Validators.required]
    }, { validators: [LookupUserExperienceTabComponent.teachingExperienceDateValidator] });

    this.businessIndustryForm = this.fb.group({
      employer_name: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(60), Validators.pattern(/^[A-Za-z\s.,\-&'()/]{2,60}$/)]]
    });

    this.uploadDocMetaForm = this.fb.group({
      category: ['', Validators.required],
      type: ['', Validators.required]
    });
  }
  outOfStateLicenseForm: FormGroup;


  ngOnInit(): void {
    this.manageProfileService
      .getBusinessRuleByKey('Documents', 'document_deletion_time_left')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: unknown) => {
          this.documentDeletionStaffHours = parseDocumentDeletionStaffHoursFromApi(res);
        },
        error: () => {
          this.documentDeletionStaffHours = null;
        }
      });
    this.loaderrorMessages();
    this.loadMetadata();
    this.loadStatesLookup();
    this.employmentForm.get('zip_code')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (!val) return;
        const digits = val.replace(/\D/g, '');
        if (digits.length === 5 || digits.length === 9) {
          this.fetchSmartyForEmploymentZip(val);
        }
      });
  }

  ngOnChanges(_: SimpleChanges): void {
    this.mapExperienceData();
    this.updateDataGridConfig();
  }
  private mapProfileUploadRowsForLookupGrid(rawRows: any[] | null | undefined, apps: any[]): any[] {
    if (!Array.isArray(rawRows) || rawRows.length === 0) {
      return [];
    }
    return rawRows.map((u: any) => {
      let internalId =
        u.application_id != null && String(u.application_id).trim() !== ''
          ? String(u.application_id).trim()
          : '';
      let matchedApp = internalId ? apps.find((a: any) => String(a.application_id) === internalId) : undefined;
      if (!matchedApp && internalId) {
        const byDisplay = apps.find(
          (a: any) => String(a.app_id ?? '').trim() !== '' && String(a.app_id).trim() === internalId
        );
        if (byDisplay) {
          matchedApp = byDisplay;
          internalId = String(byDisplay.application_id);
        }
      }
      const displayId =
        u.application_number != null && String(u.application_number).trim() !== ''
          ? String(u.application_number).trim()
          : matchedApp?.app_id != null && String(matchedApp.app_id).trim() !== ''
            ? String(matchedApp.app_id).trim()
            : internalId;
      const pickLabel = (...vals: unknown[]): string => {
        for (const v of vals) {
          if (v != null && String(v).trim() !== '') return String(v).trim();
        }
        return '';
      };
      const resolveCategoryLabel = (val: unknown): string => {
        const str = String(val ?? '').trim();
        if (!str) return '';
        const found = this.docCategoryOptions.find(c => c.key === str || c.value === str);
        return found ? found.value : str;
      };
      const resolveTypeLabel = (val: unknown): string => {
        const str = String(val ?? '').trim();
        if (!str) return '';
        const found = this.allDocTypeOptions.find(t => t.key === str || t.value === str);
        return found ? found.value : str;
      };
      const document_category = resolveCategoryLabel(pickLabel(
        u.document_category, u.category_name, u.category,
        u.str_document_category, u.cd_document_category_name, u.cd_document_category
      ));
      const document_type = resolveTypeLabel(pickLabel(
        u.document_type, u.type, u.str_document_type,
        u.cd_document_type_name, u.cd_document_type
      ));
      return {
        ...u,
        document_category,
        document_type,
        id: internalId || displayId,
        application_id: displayId || '---',
        application_number: displayId,
        application_type: u.application_type ?? matchedApp?.app_type ?? '',
        application_status: u.application_status ?? matchedApp?.app_status ?? '',
        date_submitted: matchedApp?.date_submitted ?? u.date_submitted ?? ''
      };
    });
  }

  readonly onLookupApplicationIdClick = (applicationInternalId: string, event?: Event): void => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const idStr =
      applicationInternalId != null && String(applicationInternalId).trim() !== ''
        ? String(applicationInternalId).trim()
        : '';
    if (!idStr) {
      this.alertService.warn('No application is linked to this row.');
      return;
    }
    const allSupportRows = [
      ...(this.profile_documents?.['Out of State or Country Licensure'] || []),
      ...(this.profile_documents?.['Teaching Experience'] || []),
      ...(this.profile_documents?.['Verifiable Teaching Experience'] || []),
      ...(this.profile_documents?.['Employment'] || []),
      ...(this.profile_documents?.['Verifiable Business and Industry Work Experience'] || [])
    ];
    const gridRow = allSupportRows.find((r: any) => String(r.id ?? '').trim() === idStr);
    const selectedApp = this.buildSelectedAppForOpenFromLookup(idStr, gridRow);
    this.applicationOpenFromGrid.openApplicationLikeDataGrid(selectedApp, {
      returnTargets: lookupUserReturnTargets(this.personId),
      sourceTab: 'experience',
      sourcePersonId: this.personId ?? undefined
    });
  };

  private buildSelectedAppForOpenFromLookup(internalId: string, gridRow?: any): ApplicationOpenGridRow {
    const row = this.educatorDashboardRow as any;
    const apps = Array.isArray(row?.p_application) ? row.p_application : [];
    const appMatch = apps.find((a: any) => String(a.application_id) === String(internalId));

    const rawDisplay = gridRow?.application_id != null ? String(gridRow.application_id).trim() : '';
    const displayApp =
      rawDisplay !== '' && rawDisplay !== '---'
        ? rawDisplay
        : appMatch?.app_id != null && String(appMatch.app_id).trim() !== ''
          ? String(appMatch.app_id).trim()
          : String(internalId);

    const idNumeric = normalizeNumericApplicationId(internalId);
    return {
      id: idNumeric !== null ? idNumeric : internalId,
      application_id: displayApp,
      application_number: gridRow?.application_number ?? displayApp,
      application_type: (gridRow?.application_type ?? appMatch?.app_type ?? '') as string,
      date_submitted: (gridRow?.date_submitted ?? appMatch?.date_submitted ?? '') as string,
      application_status: (gridRow?.application_status ?? appMatch?.app_status ?? '') as string,
      re_submitted_date: gridRow?.re_submitted_date as string | undefined,
      educator_name: this.educatorDisplayNameForModal(),
      is_archived: gridRow?.is_archived as boolean | undefined
    };
  }

  private educatorDisplayNameForModal(): string {
    const row = this.educatorDashboardRow as any;
    const prof = row?.p_profile;
    if (prof && typeof prof === 'object' && !Array.isArray(prof)) {
      const fn = String((prof as any).first_name ?? (prof as any).firstName ?? '').trim();
      const ln = String((prof as any).last_name ?? (prof as any).lastName ?? '').trim();
      const combined = `${fn} ${ln}`.trim();
      if (combined) {
        return combined;
      }
      const n = (prof as any).person_name;
      if (n && String(n).trim()) {
        return String(n).trim();
      }
    }
    if (Array.isArray(prof) && prof[0]?.person_name) {
      return String(prof[0].person_name).trim();
    }
    return '—';
  }

  private loadMetadata(): void {
    this.commonService
      .getMetaData(QUEUE_CONSTANTS.USER_EXPERIENCE)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((res: any) => {
        // Load headers directly from API metadata (JSON)
        this.outOfStateLicHeaders = res.outOfStateLicenseGrid;
        this.outOfStateLicSupportHeaders = res.outOfStateLicenseSupportDocGrid;
        this.teachingExperienceHeaders = res.teachingExperienceGrid;
        this.teachingExperienceSupportHeaders = res.teachingExperienceSupportDocGrid;
        this.assignmentExperienceHeaders = res.assignmentExperienceGrid;
        this.employmentHeaders = res.employmentGrid;
        this.employmentSupportHeaders = res.employmentSupportDocGrid;
        this.verifiableTeachingExpHeaders = res.verifiableTeachingExperienceGrid;
        this.verifiableTeachingExpSupportHeaders = res.verifiableTeachingExperienceSupportDocGrid;
        this.businessIndustryHeaders = res.verifiableBusinessIndustryGrid;
        this.businessIndustrySupportHeaders = res.verifiableBusinessIndustrySupportDocGrid;

        // Initialize visible columns for Out-of-State License
        if (Array.isArray(this.outOfStateLicHeaders)) {
          this.allOutOfStateLicColumns = this.outOfStateLicHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allOutOfStateLicColumns);
        }
        if (!this.visibleOutOfStateLicColumns) {
          this.visibleOutOfStateLicColumns = {};
        }
        this.allOutOfStateLicColumns.forEach(c => {
          if (typeof this.visibleOutOfStateLicColumns[c.key] === 'undefined') {
            this.visibleOutOfStateLicColumns[c.key] = true;
          }
        });

        // Initialize visible columns for Teaching Experience
        if (Array.isArray(this.teachingExperienceHeaders)) {
          this.allTeachingExpColumns = this.teachingExperienceHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allTeachingExpColumns);
        }
        if (!this.visibleTeachingExpColumns) {
          this.visibleTeachingExpColumns = {};
        }
        this.allTeachingExpColumns.forEach(c => {
          if (typeof this.visibleTeachingExpColumns[c.key] === 'undefined') {
            this.visibleTeachingExpColumns[c.key] = true;
          }
        });

        // Initialize visible columns for Assignment Experience
        if (Array.isArray(this.assignmentExperienceHeaders)) {
          this.allAssignmentExpColumns = this.assignmentExperienceHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allAssignmentExpColumns);
        }
        if (!this.visibleAssignmentExpColumns) {
          this.visibleAssignmentExpColumns = {};
        }
        this.allAssignmentExpColumns.forEach(c => {
          if (typeof this.visibleAssignmentExpColumns[c.key] === 'undefined') {
            this.visibleAssignmentExpColumns[c.key] = true;
          }
        });

        // Initialize visible columns for Employment
        if (Array.isArray(this.employmentHeaders)) {
          this.allEmploymentColumns = this.employmentHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allEmploymentColumns);
        }
        if (!this.visibleEmploymentColumns) {
          this.visibleEmploymentColumns = {};
        }
        this.allEmploymentColumns.forEach(c => {
          if (typeof this.visibleEmploymentColumns[c.key] === 'undefined') {
            this.visibleEmploymentColumns[c.key] = true;
          }
        });

        // Initialize supporting columns for Out-of-State Licensure
        if (Array.isArray(this.outOfStateLicSupportHeaders)) {
          this.allOutOfStateLicSupportColumns = this.outOfStateLicSupportHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allOutOfStateLicSupportColumns);
        }
        if (!this.visibleOutOfStateLicSupportColumns) {
          this.visibleOutOfStateLicSupportColumns = {};
        }
        this.allOutOfStateLicSupportColumns.forEach(c => {
          if (typeof this.visibleOutOfStateLicSupportColumns[c.key] === 'undefined') {
            this.visibleOutOfStateLicSupportColumns[c.key] = true;
          }
        });

        // Initialize supporting columns for Teaching Experience
        if (Array.isArray(this.teachingExperienceSupportHeaders)) {
          this.allTeachingExpSupportColumns = this.teachingExperienceSupportHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allTeachingExpSupportColumns);
        }
        if (!this.visibleTeachingExpSupportColumns) {
          this.visibleTeachingExpSupportColumns = {};
        }
        this.allTeachingExpSupportColumns.forEach(c => {
          if (typeof this.visibleTeachingExpSupportColumns[c.key] === 'undefined') {
            this.visibleTeachingExpSupportColumns[c.key] = true;
          }
        });

        // Initialize supporting columns for Employment
        if (Array.isArray(this.employmentSupportHeaders)) {
          this.allEmploymentSupportColumns = this.employmentSupportHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allEmploymentSupportColumns);
        }
        if (!this.visibleEmploymentSupportColumns) {
          this.visibleEmploymentSupportColumns = {};
        }
        this.allEmploymentSupportColumns.forEach(c => {
          if (typeof this.visibleEmploymentSupportColumns[c.key] === 'undefined') {
            this.visibleEmploymentSupportColumns[c.key] = true;
          }
        });

        // Initialize visible columns for Verifiable Teaching Experience
        if (Array.isArray(this.verifiableTeachingExpHeaders)) {
          this.allVerifiableTeachingExpColumns = this.verifiableTeachingExpHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allVerifiableTeachingExpColumns);
        }
        if (!this.visibleVerifiableTeachingExpColumns) {
          this.visibleVerifiableTeachingExpColumns = {};
        }
        this.allVerifiableTeachingExpColumns.forEach(c => {
          if (typeof this.visibleVerifiableTeachingExpColumns[c.key] === 'undefined') {
            this.visibleVerifiableTeachingExpColumns[c.key] = true;
          }
        });

        // Initialize supporting columns for Verifiable Teaching Experience
        if (Array.isArray(this.verifiableTeachingExpSupportHeaders)) {
          this.allVerifiableTeachingExpSupportColumns = this.verifiableTeachingExpSupportHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allVerifiableTeachingExpSupportColumns);
        }
        if (!this.visibleVerifiableTeachingExpSupportColumns) {
          this.visibleVerifiableTeachingExpSupportColumns = {};
        }
        this.allVerifiableTeachingExpSupportColumns.forEach(c => {
          if (typeof this.visibleVerifiableTeachingExpSupportColumns[c.key] === 'undefined') {
            this.visibleVerifiableTeachingExpSupportColumns[c.key] = true;
          }
        });

        // Initialize visible columns for Verifiable Business and Industry
        if (Array.isArray(this.businessIndustryHeaders)) {
          this.allBusinessIndustryColumns = this.businessIndustryHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allBusinessIndustryColumns);
        }
        if (!this.visibleBusinessIndustryColumns) {
          this.visibleBusinessIndustryColumns = {};
        }
        this.allBusinessIndustryColumns.forEach(c => {
          if (typeof this.visibleBusinessIndustryColumns[c.key] === 'undefined') {
            this.visibleBusinessIndustryColumns[c.key] = true;
          }
        });

        // Initialize supporting columns for Verifiable Business and Industry
        if (Array.isArray(this.businessIndustrySupportHeaders)) {
          this.allBusinessIndustrySupportColumns = this.businessIndustrySupportHeaders.map((h: any) => ({
            key: h.dataField,
            label: h.caption || h.dataField
          }));
          this.lockFirstColumn(this.allBusinessIndustrySupportColumns);
        }
        if (!this.visibleBusinessIndustrySupportColumns) {
          this.visibleBusinessIndustrySupportColumns = {};
        }
        this.allBusinessIndustrySupportColumns.forEach(c => {
          if (typeof this.visibleBusinessIndustrySupportColumns[c.key] === 'undefined') {
            this.visibleBusinessIndustrySupportColumns[c.key] = true;
          }
        });

        this.mapExperienceData();
        this.updateDataGridConfig();
      });
  }

  //  private loadStatesLookup(): void {
  //   // Call lookup API to fetch states with request type 'STATE'
  //   this.httpService.post('/api/lookup', { requestType: 'STATE' })
  //     .pipe(take(1), takeUntil(this.destroy$))
  //     .subscribe({
  //       next: (response: any) => {
  //         // Populate statesList from API response
  //         // Expected format: { key: '11', value: 'Florida' } array
  //         if (Array.isArray(response)) {
  //           this.statesList = response.map((item: any) => ({
  //             key: String(item.key || item.id),
  //             value: item.value || item.label || item.name
  //           }));
  //         } else if (response && typeof response === 'object') {
  //           // If response is an object with key-value pairs, convert to array
  //           this.statesList = Object.entries(response).map(([key, value]) => ({
  //             key,
  //             value: String(value)
  //           }));
  //         }
  //         this.cd.detectChanges();
  //       },
  //       error: (error: any) => {
  //         console.error('Error loading states lookup:', error);
  //       }
  //     });
  // }
  profile_documents: Record<string, any> = [];
  private mapExperienceData(): void {
    const source = this.educatorDashboardRow as Record<string, unknown> | null;
    if (!source) {
      this.profile_documents = {}
    }
    const apps = Array.isArray(this.educatorDashboardRow?.['p_application'])
      ? this.educatorDashboardRow['p_application']
      : [];
    const allUploads = Array.isArray(this.educatorDashboardRow?.['p_profile_upload'])
      ? this.educatorDashboardRow['p_profile_upload']
      : [];
    const rawUploadGrouped = _.groupBy(allUploads, 'profile_tab_section');

    /**
     * Filter documents for Out of State or Country Licensure section.
     * Includes documents by profile_tab_section or by document_category.
     */
    const filterOosLicensureDocuments = (docs: any[]): any[] => {
      const lower = (x: unknown) => String(x ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
      return docs.filter((d: any) => {
        const p = lower(d.profile_tab_section);
        const cat = lower(d.document_category ?? d.category ?? d.category_name ?? '');
        // Match by profile_tab_section
        const matchesSection = (
          p === 'out of state or country licensure' ||
          p === 'out-of-state-licensure' ||
          (p.includes('licensure') && (p.includes('out of state') || p.includes('out-of-state') || p.includes('out_of_state')))
        );
        // Match by document category
        const matchesCategory = (
          cat === 'out-of-state or country licensure' ||
          cat === 'out of state or country licensure' ||
          (cat.includes('licensure') && (cat.includes('out of state') || cat.includes('out-of-state') || cat.includes('out_of_state')))
        );
        return matchesSection || matchesCategory;
      });
    };

    const mapSection = (key: string) =>
      this.mapProfileUploadRowsForLookupGrid(rawUploadGrouped[key] ?? [], apps);
    const mapOosLicensureSection = () =>
      this.mapProfileUploadRowsForLookupGrid(filterOosLicensureDocuments(allUploads), apps);

    this.profile_documents = {
      'Out of State or Country Licensure':                       mapOosLicensureSection(),
      'Teaching Experience':                                     mapSection('Teaching Experience'),
      'Verifiable Teaching Experience':                          mapSection('Verifiable Teaching Experience'),
      'Employment':                                              mapSection('Employment'),
      'Verifiable Business and Industry Work Experience':        mapSection('Verifiable Business and Industry Work Experience')
    };
    // Map Out-of-State License data
    if (source && Array.isArray(source['p_profile_out_of_state_license'])) {
      this.outOfStateLicRows = (source['p_profile_out_of_state_license'] as Record<string, unknown>[]).map(item => ({
        ...item,
        updated_by: item['updated_by'] || '---'
      }));
    } else {
      this.outOfStateLicRows = [];
    }

    // Map Teaching Experience data
    if (source && Array.isArray(source['p_teaching_experience'])) {
      this.teachingExperienceRows = (source['p_teaching_experience'] as Record<string, unknown>[]).map(item => ({
        ...item,
        from: this.pickValue(item, ['from_date_of_service', 'from_date', 'start_date']),
        to: this.pickValue(item, ['to_date_of_service', 'to_date', 'end_date']),
        updated_by: item['updated_by'] || '---'
      }));
    } else {
      this.teachingExperienceRows = [];
    }

    // Map Assignment Experience data (from p_assignment_experience)
    if (source && Array.isArray(source['p_assignment_experience'])) {
      this.assignmentExperienceRows = (source['p_assignment_experience'] as Record<string, unknown>[]).map(item => ({
        district: item['district'],
        school: item['school'],
        assignment: item['assignment'],
        school_year: item['school_year'],
        hours: item['hours'],
        fte: item['fte'],
        contract_amount: (() => { const v = item['salary_schedule'] ?? item['contract_amount']; return v != null && v !== '' ? '$' + v : v; })()
      }));
    } else {
      this.assignmentExperienceRows = [];
    }

    // Map Employment data (from p_experience_detail)
    if (source && Array.isArray(source['p_experience_detail'])) {
      this.employmentRows = (source['p_experience_detail'] as Record<string, unknown>[]).map(item => ({
        employment_experience_id: item['employment_experience_id'] ?? item['experience_id'],
        experience_id: item['experience_id'],
        employer_name: item['employer_name'],
        from: this.pickValue(item, ['from_date_of_service', 'from_date', 'start_date']),
        to: this.pickValue(item, ['to_date_of_service', 'to_date', 'end_date']),
        city: item['city'],
        state: item['state'],
        zip_code: item['zip_code'],
        updated_by: item['updated_by'] || '---'
      }));
    } else {
      this.employmentRows = [];
    }

    // Map Verifiable Teaching Experience data
    if (source && Array.isArray(source['p_teaching_experience'])) {
      this.verifiableTeachingExpRows = (source['p_teaching_experience'] as Record<string, unknown>[]).map(item => ({
        ...item,
        district: String(item['district'] || '').trim() || '---',
        school: String(item['school'] || '').trim() || '---',
        school_name: String(item['school_name'] || '').trim() || '---',
        from: this.pickValue(item, ['from_date_of_service', 'from_date', 'start_date']),
        to: this.pickValue(item, ['to_date_of_service', 'to_date', 'end_date']),
        updated_by: item['updated_by'] || '---'
      }));
    } else {
      this.verifiableTeachingExpRows = [];
    }

    // Map Verifiable Business and Industry Work Experience data
    if (source && Array.isArray(source['p_profile_business_industry_experience'])) {
      this.businessIndustryRows = (source['p_profile_business_industry_experience'] as Record<string, unknown>[]).map(item => ({
        ...item,
        updated_by: item['updated_by'] || '---'
      }));
    } else {
      this.businessIndustryRows = [];
    }
  }

  private pickValue(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const val = source[key];
      if (val !== null && val !== undefined && String(val).trim() !== '') {
        if (typeof val === 'number' && isNaN(val)) continue;
        const str = String(val).trim();
        if (str.toLowerCase() === 'nan') continue;
        return str;
      }
    }
    return '';
  }

  private keyByValue(list: { key: string; value: string }[], display: string): string {
    if (!display) return '';
    const found = list.find(l => l.value === display || l.key === display);
    return found ? found.key : display;
  }

  private normalizeStr(val: unknown): string {
    if (val === null || val === undefined) return '';
    return String(val).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  private isVerifiableTeachingDuplicate(): boolean {
    const fv = this.verifiableTeachingExperienceForm.value;
    // Normalize '---' (used for empty display values in rows) to '' so it matches null form values
    const n = (val: unknown): string => {
      if (val === null || val === undefined || val === '---') return '';
      return String(val).toLowerCase().trim().replace(/\s+/g, ' ');
    };
    return this.verifiableTeachingExpRows.some((row: any) =>
      n(this.keyByValue(this.verifiableTeachingStatesList, row['state'])) === n(fv.state) &&
      n(this.formatDateForInput(row['from'] || row['from_date_of_service'] || row['from_date'] || row['start_date'])) === n(fv.from) &&
      n(this.formatDateForInput(row['to'] || row['to_date_of_service'] || row['to_date'] || row['end_date'])) === n(fv.to) &&
      n(this.keyByValue(this.verifiableTeachingStatusLookup, row['status'])) === n(fv.status) &&
      n(this.keyByValue(this.districtLookup, row['district'])) === n(fv.district) &&
      n(this.keyByValue(this.schoolLookup, row['school'])) === n(fv.school)
    );
  }

  private isOutOfStateLicenseDuplicate(): boolean {
    const fv = this.outOfStateLicenseForm.value;
    return this.outOfStateLicRows.some((row: any) =>
      this.normalizeStr(this.keyByValue(this.statesList, row['state'])) === this.normalizeStr(fv.state) &&
      this.normalizeStr(row['license_number']) === this.normalizeStr(fv.license_number) &&
      this.normalizeStr(row['license_status']) === this.normalizeStr(fv.license_status)
    );
  }

  private isBusinessIndustryDuplicate(): boolean {
    const fv = this.businessIndustryForm.value;
    return this.businessIndustryRows.some((row: any) =>
      this.normalizeStr(row['employer_name']) === this.normalizeStr(fv.employer_name)
    );
  }

  private isEmploymentDuplicate(): boolean {
    const fv = this.employmentForm.value;
    return this.employmentRows.some((row: any) =>
      this.normalizeStr(row['employer_name']) === this.normalizeStr(fv.employer_name) &&
      this.normalizeStr(this.formatDateForInput(row['from'] || row['from_date_of_service'] || row['from_date'] || row['start_date'])) === this.normalizeStr(fv.from) &&
      this.normalizeStr(this.formatDateForInput(row['to'] || row['to_date_of_service'] || row['to_date'] || row['end_date'])) === this.normalizeStr(fv.to) &&
      this.normalizeStr(row['city']) === this.normalizeStr(fv.city) &&
      this.normalizeStr(this.keyByValue(this.statesList, row['state'])) === this.normalizeStr(fv.state) &&
      this.normalizeStr(row['zip_code']) === this.normalizeStr(fv.zip_code)
    );
  }

  private pickId(source: Record<string, unknown>, keys: string[]): any {
    for (const key of keys) {
      const val = source[key];
      if (val !== null && val !== undefined) {
        return val;
      }
    }
    return null;
  }

  onColumnClick(text: string, key: any): void {
    const allColsMap: Record<string, { key: string; disabled?: boolean }[]> = {
      visibleOutOfStateLicColumns: this.allOutOfStateLicColumns,
      visibleOutOfStateLicSupportColumns: this.allOutOfStateLicSupportColumns,
      visibleTeachingExpColumns: this.allTeachingExpColumns,
      visibleTeachingExpSupportColumns: this.allTeachingExpSupportColumns,
      visibleAssignmentExpColumns: this.allAssignmentExpColumns,
      visibleEmploymentColumns: this.allEmploymentColumns,
      visibleEmploymentSupportColumns: this.allEmploymentSupportColumns,
      visibleVerifiableTeachingExpColumns: this.allVerifiableTeachingExpColumns,
      visibleVerifiableTeachingExpSupportColumns: this.allVerifiableTeachingExpSupportColumns,
      visibleBusinessIndustryColumns: this.allBusinessIndustryColumns,
      visibleBusinessIndustrySupportColumns: this.allBusinessIndustrySupportColumns,
    };
    const cols = allColsMap[text];
    if (cols?.find(c => c.key === key)?.disabled) return;
    const current = !!this[text][key];
    if (!this[text]) this[text] = {};
    this[text][key] = !current;
    this.updateDataGridConfig();
    this.cd.detectChanges();
  }

  private lockFirstColumn(cols: { key: string; label: string; disabled?: boolean }[]): void {
    if (cols.length) cols[0].disabled = true;
  }

  closeColumnChooser(property: string): void {
    this[property] = false;
  }

  updateDataGridConfig(): void {
    this.outOfStateLicDataGrid = DataGridVO.create({
      customHeaders: this.outOfStateLicHeaders,
      data: this.outOfStateLicRows,
      visibleColumns: this.visibleOutOfStateLicColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.outOfStateLicSupportGrid = DataGridVO.create({
      customHeaders: this.outOfStateLicSupportHeaders,
      data: this.profile_documents?.["Out of State or Country Licensure"],
      visibleColumns: this.visibleOutOfStateLicSupportColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.teachingExperienceDataGrid = DataGridVO.create({
      customHeaders: this.teachingExperienceHeaders,
      data: this.teachingExperienceRows,
      visibleColumns: this.visibleTeachingExpColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.teachingExperienceSupportGrid = DataGridVO.create({
      customHeaders: this.teachingExperienceSupportHeaders,
      data: this.profile_documents?.["Teaching Experience"],
      visibleColumns: this.visibleTeachingExpSupportColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.assignmentExperienceDataGrid = DataGridVO.create({
      customHeaders: this.assignmentExperienceHeaders,
      data: this.assignmentExperienceRows,
      visibleColumns: this.visibleAssignmentExpColumns,
      showQueueActions: false,
      showActionsColumn: false,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: true
    });

    this.employmentDataGrid = DataGridVO.create({
      customHeaders: this.employmentHeaders,
      data: this.employmentRows,
      visibleColumns: this.visibleEmploymentColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.employmentSupportGrid = DataGridVO.create({
      customHeaders: this.employmentSupportHeaders,
      data: this.profile_documents?.["Employment"],
      visibleColumns: this.visibleEmploymentSupportColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.verifiableTeachingExpDataGrid = DataGridVO.create({
      customHeaders: this.verifiableTeachingExpHeaders,
      data: this.verifiableTeachingExpRows,
      visibleColumns: this.visibleVerifiableTeachingExpColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.verifiableTeachingExpSupportGrid = DataGridVO.create({
      customHeaders: this.verifiableTeachingExpSupportHeaders,
      data: this.profile_documents?.["Verifiable Teaching Experience"],
      visibleColumns: this.visibleVerifiableTeachingExpSupportColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.businessIndustryDataGrid = DataGridVO.create({
      customHeaders: this.businessIndustryHeaders,
      data: this.businessIndustryRows,
      visibleColumns: this.visibleBusinessIndustryColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });

    this.businessIndustrySupportGrid = DataGridVO.create({
      customHeaders: this.businessIndustrySupportHeaders,
      data: this.profile_documents?.["Verifiable Business and Industry Work Experience"],
      visibleColumns: this.visibleBusinessIndustrySupportColumns,
      showQueueActions: true,
      role: 'lookup-experience-logs',
      activeTab: 'lookup-experience-logs',
      showPaging: false
    });
  }

  formatDateInput(event: Event, key: string, mainKey: string): void {
    const target = event.target as HTMLInputElement;
    const value = (target?.value || '').replace(/\D/g, '');
    const limited = value.length > 8 ? value.substring(0, 8) : value;

    let formatted = limited;
    if (limited.length >= 5) {
      formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}/${limited.substring(4, 8)}`;
    } else if (limited.length >= 3) {
      formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}`;
    } else if (limited.length >= 1) {
      formatted = limited.substring(0, 2);
    }

    // ✅ ONLY validate when date is complete
    if (formatted.length === 10) {
      let fromValue;
      if (mainKey === 'Teaching') {
        fromValue = this.teachingExperienceForm.get('from')?.value;
      } else if (mainKey === 'VerifiableTeaching') {
        fromValue = this.verifiableTeachingExperienceForm.get('from')?.value;
      } else {
        fromValue = this.employmentForm.get('from')?.value;
      }

      if (fromValue && fromValue.length === 10) {
        const [fm, fd, fy] = fromValue.split('/').map(Number);
        const [tm, td, ty] = formatted.split('/').map(Number);

        const fromDate = new Date(fy, fm - 1, fd);
        const toDate = new Date(ty, tm - 1, td);

        // ❌ To date before From date → reject
        if (mainKey === 'Teaching') {
          console.log('Validating Teaching Dates:', { fromDate, toDate });
          if (toDate < fromDate) {
            this.teachingExperienceForm.get('to')?.setErrors({ toBeforeFrom: true });
            return;
          }
        }
        else if (mainKey === 'VerifiableTeaching') {
          console.log('Validating Verifiable Teaching Dates:', { fromDate, toDate });
          if (toDate < fromDate) {
            this.verifiableTeachingExperienceForm.get('to')?.setErrors({ toBeforeFrom: true });
            return;
          }
        }
        else if (mainKey === 'Employment') {
          console.log('Validating Employment Dates:', { fromDate, toDate });
          if (toDate < fromDate) {
            this.employmentForm.get('to')?.setErrors({ toBeforeFrom: true });
            return;
          }
        }
      }
    }


    // ✅ Set value normally
    if (mainKey === 'Teaching') {
      if (key === 'TeachingFrom') {
        this.teachingExperienceForm.get('from')?.setValue(formatted, { emitEvent: false });
        this.teachingExperienceForm.get('from')?.markAsUntouched({ onlySelf: true });
      } else if (key === 'TeachingTo') {
        this.teachingExperienceForm.get('to')?.setValue(formatted, { emitEvent: false });
        this.teachingExperienceForm.get('to')?.markAsUntouched({ onlySelf: true });
      }
    } else if (mainKey === 'VerifiableTeaching') {
      if (key === 'VerifiableTeachingFrom') {
        this.verifiableTeachingExperienceForm.get('from')?.setValue(formatted, { emitEvent: false });
        this.verifiableTeachingExperienceForm.get('from')?.markAsUntouched({ onlySelf: true });
        this.calculateVerifiableTeachingTotalYears();
      } else if (key === 'VerifiableTeachingTo') {
        this.verifiableTeachingExperienceForm.get('to')?.setValue(formatted, { emitEvent: false });
        this.verifiableTeachingExperienceForm.get('to')?.markAsUntouched({ onlySelf: true });
        this.calculateVerifiableTeachingTotalYears();
      }
    } else if (mainKey === 'Employment') {
      if (key === 'EmploymentFrom') {
        this.employmentForm.get('from')?.setValue(formatted, { emitEvent: false });
        this.employmentForm.get('from')?.markAsUntouched({ onlySelf: true });
      } else if (key === 'EmploymentTo') {
        this.employmentForm.get('to')?.setValue(formatted, { emitEvent: false });
        this.employmentForm.get('to')?.markAsUntouched({ onlySelf: true });
      }
    }
  }

  onDateBlur(key: string, mainKey: string): void {
    let control: AbstractControl | null = null;
    let fromValue: string | null = null;
    let toValue: string | null = null;
    let toControl: AbstractControl | null = null;

    if (mainKey === 'Teaching') {
      control = key === 'TeachingFrom' ? this.teachingExperienceForm.get('from') : this.teachingExperienceForm.get('to');
      fromValue = this.teachingExperienceForm.get('from')?.value ?? null;
      toValue = this.teachingExperienceForm.get('to')?.value ?? null;
      toControl = this.teachingExperienceForm.get('to');
    } else if (mainKey === 'VerifiableTeaching') {
      control = key === 'VerifiableTeachingFrom' ? this.verifiableTeachingExperienceForm.get('from') : this.verifiableTeachingExperienceForm.get('to');
      fromValue = this.verifiableTeachingExperienceForm.get('from')?.value ?? null;
      toValue = this.verifiableTeachingExperienceForm.get('to')?.value ?? null;
      toControl = this.verifiableTeachingExperienceForm.get('to');
    } else if (mainKey === 'Employment') {
      control = key === 'EmploymentFrom' ? this.employmentForm.get('from') : this.employmentForm.get('to');
      fromValue = this.employmentForm.get('from')?.value ?? null;
      toValue = this.employmentForm.get('to')?.value ?? null;
      toControl = this.employmentForm.get('to');
    }

    if (control) {
      control.markAsTouched({ onlySelf: true });
      control.updateValueAndValidity();
      // Flag incomplete dates (e.g. "04/02" — not yet a full MM/DD/YYYY)
      const val = control.value;
      if (val && typeof val === 'string' && val.length > 0 && val.length < 10) {
        control.setErrors({ ...control.errors, incompleteDate: true });
      }
    }

    // Re-apply toBeforeFrom after updateValueAndValidity (which clears manually-set errors)
    if (toControl && fromValue?.length === 10 && toValue?.length === 10) {
      const parseParts = (v: string): Date | null => {
        const p = v.split('/');
        if (p.length !== 3) return null;
        const [m, d, y] = p.map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setHours(0, 0, 0, 0);
        return dt;
      };
      const fromDate = parseParts(fromValue);
      const toDate = parseParts(toValue);
      if (fromDate && toDate && toDate.getTime() < fromDate.getTime()) {
        toControl.markAsTouched({ onlySelf: true });
        toControl.setErrors({ ...toControl.errors, toBeforeFrom: true });
      }
    }
  }

  currentDate: string = new Date().toISOString().split('T')[0];
  openDatePicker(key: string, mainKey: string, event?: Event): void {
    console.log("date picker open for", key, mainKey);
    if (event) {
      event.stopPropagation();
    }
    let date_Input
    if (mainKey === 'Teaching') {
      date_Input = key === 'TeachingFrom' ? document.getElementById('fromDate') : document.getElementById('teachingTo');
    } else if (mainKey === 'VerifiableTeaching') {
      date_Input = key === 'VerifiableTeachingFrom'
        ? (document.getElementById('verifiableTeachingFromModal') || document.getElementById('verifiableTeachingFrom'))
        : (document.getElementById('verifiableTeachingToModal') || document.getElementById('verifiableTeachingTo'));
    } else if (mainKey === 'Employment') {
      date_Input = key === 'EmploymentFrom' ? document.getElementById('EmploymentFrom') : document.getElementById('EmploymentTo');
    }
    if (!date_Input) return;
    const rect = date_Input.getBoundingClientRect();
    const dateInput = document.createElement('input') as HTMLInputElement;
    dateInput.type = 'date';
    dateInput.style.position = 'absolute';
    dateInput.style.top = rect.top + window.scrollY + 5 + 'px';
    dateInput.style.left = rect.left + 'px';
    dateInput.style.width = '120px';
    dateInput.style.height = '35px';
    dateInput.style.opacity = '0.01';
    dateInput.style.border = 'none';
    dateInput.style.zIndex = '9999';
    dateInput.style.pointerEvents = 'auto';
    if (mainKey === 'Teaching') {
      if (key === 'TeachingFrom') {
        // ✅ Past dates only
        dateInput.max = this.currentDate; // yyyy-mm-dd
      } else {
        // ✅ Any date (remove restriction)
        dateInput.max = this.currentDate;
        const fromValue = this.teachingExperienceForm.get('from')?.value;
        if (fromValue && fromValue.includes('/')) {
          const parts = fromValue.split('/');
          if (parts.length === 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            const year = parts[2];
            dateInput.min = `${year}-${month}-${day}`;
          }
        }
      }
    } else if (mainKey === 'VerifiableTeaching') {
      if (key === 'VerifiableTeachingFrom') {
        dateInput.max = this.currentDate;
      } else {
        dateInput.max = this.currentDate;
        const fromValue = this.verifiableTeachingExperienceForm.get('from')?.value;
        if (fromValue && fromValue.includes('/')) {
          const parts = fromValue.split('/');
          if (parts.length === 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            const year = parts[2];
            dateInput.min = `${year}-${month}-${day}`;
          }
        }
      }
    } else if (mainKey === 'Employment') {
      if (key === 'EmploymentFrom') {
        // ✅ Past dates only
        dateInput.max = this.currentDate; // yyyy-mm-dd
      } else {
        dateInput.max = this.currentDate;
        const fromValue = this.employmentForm.get('from')?.value;
        if (fromValue && fromValue.includes('/')) {
          const parts = fromValue.split('/');
          if (parts.length === 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            const year = parts[2];
            dateInput.min = `${year}-${month}-${day}`;
          }
        }
      }
    }
    // ✅ Pre-fill existing value
    let currentValue
    if (mainKey === 'Teaching') {
      currentValue = key === 'TeachingFrom' ? this.teachingExperienceForm.get('from')?.value : this.teachingExperienceForm.get('to')?.value;
    } else if (mainKey === 'VerifiableTeaching') {
      currentValue = key === 'VerifiableTeachingFrom' ? this.verifiableTeachingExperienceForm.get('from')?.value : this.verifiableTeachingExperienceForm.get('to')?.value;
    } else if (mainKey === 'Employment') {
      currentValue = key === 'EmploymentFrom' ? this.employmentForm.get('from')?.value : this.employmentForm.get('to')?.value;
    }

    if (currentValue && currentValue.includes('/')) {
      const parts = currentValue.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        dateInput.value = `${year}-${month}-${day}`;
      }
    }
    document.body.appendChild(dateInput);
    const cleanup = () => {
      if (document.body.contains(dateInput)) {
        dateInput.removeEventListener('change', changeHandler);
        dateInput.removeEventListener('blur', blurHandler);
        document.body.removeChild(dateInput);
      }
    };
    const changeHandler = (changeEvent: Event) => {
      const target = (changeEvent.target as HTMLInputElement);
      const selectedDate = target?.value;
      if (selectedDate) {
        const dateParts = selectedDate.split('-');
        if (dateParts.length === 3) {
          const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
          if (mainKey === 'Teaching') {
            if (key === 'TeachingFrom') {
              this.teachingExperienceForm.get('from')?.setValue(formattedDate);
              this.teachingExperienceForm.get('from')?.markAsTouched();
              this.teachingExperienceForm.get('from')?.updateValueAndValidity();
            } else {
              this.teachingExperienceForm.get('to')?.setValue(formattedDate);
              this.teachingExperienceForm.get('to')?.markAsTouched();
              this.teachingExperienceForm.get('to')?.updateValueAndValidity();
            }
          } else if (mainKey === 'VerifiableTeaching') {
            if (key === 'VerifiableTeachingFrom') {
              this.verifiableTeachingExperienceForm.get('from')?.setValue(formattedDate);
              this.verifiableTeachingExperienceForm.get('from')?.markAsTouched();
              this.verifiableTeachingExperienceForm.get('from')?.updateValueAndValidity();
              this.calculateVerifiableTeachingTotalYears();
            } else {
              this.verifiableTeachingExperienceForm.get('to')?.setValue(formattedDate);
              this.verifiableTeachingExperienceForm.get('to')?.markAsTouched();
              this.verifiableTeachingExperienceForm.get('to')?.updateValueAndValidity();
              this.calculateVerifiableTeachingTotalYears();
            }
          } else if (mainKey === 'Employment') {
            if (key === 'EmploymentFrom') {
              this.employmentForm.get('from')?.setValue(formattedDate);
              this.employmentForm.get('from')?.markAsTouched();
              this.employmentForm.get('from')?.updateValueAndValidity();
            } else {
              this.employmentForm.get('to')?.setValue(formattedDate);
              this.employmentForm.get('to')?.markAsTouched();
              this.employmentForm.get('to')?.updateValueAndValidity();
            }
          }

        }
      }
      cleanup();
    };
    const blurHandler = () => setTimeout(cleanup, 100);
    dateInput.addEventListener('change', changeHandler);
    dateInput.addEventListener('blur', blurHandler);
    dateInput.focus();
    const inputEl = dateInput as HTMLInputElement;
    if (typeof (inputEl as any).showPicker === 'function') {
      try {
        (inputEl as any).showPicker();
      } catch {
        inputEl.click();
      }
    } else {
      inputEl.click();
    }
  }
  downloadExcel(allColumns: string, visibleColumns: string, gridRefName: string): void {
    const visibleCols = this[allColumns].filter(
      (col: any) => this[visibleColumns][col.key]
    );
    if (visibleCols.length === 0) {
      window.alert('No columns selected for export');
      return;
    }
    const gridComp: DataGridComponent = this[gridRefName];
    if (
      gridComp &&
      gridComp.dataGrid &&
      gridComp.dataGrid.instance &&
      typeof gridComp.exportFilteredDataToExcel === 'function'
    ) {
      const headers = gridComp.config?.customHeaders || [];
      const columns = visibleCols.map((col: any) => {
        const header = headers.find((h: any) => h.dataField === col.key);
        return {
          label: col.label,
          key: col.key,
          dataType: header?.dataType
        };
      });
      const sheetNameMap: { [key: string]: string } = {
        outOfStateLicGridRef: 'Out_Of_State_Licensure.xlsx',
        outOfStateLicSupportGridComp: 'Out_Of_State_Licensure_Supporting_Docs.xlsx',
        assignmentExpGridRef: 'Assignment_Experience.xlsx',
        verifiableTeachingExpGridRef: 'Verifiable_Teaching_Experience.xlsx',
        verifiableTeachingExpSupportGridComp: 'Verifiable_Teaching_Experience_Supporting_Docs.xlsx',
        employmentGridRef: 'Employment.xlsx',
        employmentSupportGridComp: 'Employment_Supporting_Docs.xlsx',
        businessIndustryGridRef: 'Business_Industry_Work_Experience.xlsx',
        businessIndustrySupportGridComp: 'Business_Industry_Supporting_Docs.xlsx'
      };
      const sheetName = sheetNameMap[gridRefName] || 'ExperienceLogs.xlsx';
      const gridInstance = gridComp.dataGrid.instance;
      const oldPageSize = gridInstance.pageSize ? gridInstance.pageSize() : null;
      const totalCount = gridInstance.totalCount ? gridInstance.totalCount() : null;
      if (oldPageSize && totalCount && oldPageSize < totalCount) {
        gridInstance.pageSize(totalCount);
        setTimeout(() => {
          gridComp.exportFilteredDataToExcel(columns, sheetName);
          setTimeout(() => {
            gridInstance.pageSize(oldPageSize);
          }, 500);
        }, 200);
        return;
      }
      gridComp.exportFilteredDataToExcel(columns, sheetName);
    }
  }
  private loadStatesLookup(): void {
    this.httpService.get(`${API_ENDPOINTS.DOMAIN_LOOKUP_BY_TYPE}OUT OF STATE TEST`)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const lookupData = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];

          this.statesList = lookupData
            .filter((item: any) => item?.key !== undefined && item?.value !== undefined)
            .map((item: any) => ({ key: String(item.key), value: String(item.value) }))
            .sort((a: any, b: any) => a.value.localeCompare(b.value));
          this.filteredVerifiableTeachingStates = [...this.statesList];

          this.stateFilterCtrl.valueChanges.subscribe(() => {
            this.filterVerifiableTeachingStates();
          });

          this.cd.detectChanges();
        },
        error: (error: any) => {
          console.error('Error loading states lookup:', error);
        }
      });
  }

  filterVerifiableTeachingStates(): void {
    const search = this.stateFilterCtrl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredVerifiableTeachingStates = [...this.statesList];
    } else {
      this.filteredVerifiableTeachingStates = this.statesList.filter(s => s.value.toLowerCase().includes(search));
    }
  }

  filterVerifiableTeachingExpStates(): void {
    const search = this.verifiableTeachingExpStateFilterCtrl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredVerifiableTeachingExpStates = [...this.verifiableTeachingStatesList];
    } else {
      this.filteredVerifiableTeachingExpStates = this.verifiableTeachingStatesList.filter(s => s.value.toLowerCase().includes(search));
    }
  }

  onStateSelectOpened(opened: boolean): void {
    if (opened) {
      this.stateFilterCtrl.setValue('');
      this.filteredVerifiableTeachingStates = [...this.statesList];
    }
  }

  onVerifiableTeachingExpStateSelectOpened(opened: boolean): void {
    if (opened) {
      this.verifiableTeachingExpStateFilterCtrl.setValue('');
      this.filteredVerifiableTeachingExpStates = [...this.verifiableTeachingStatesList];
    }
  }

  filterDistricts(): void {
    const search = this.districtFilterCtrl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredDistricts = [...this.districtLookup];
    } else {
      this.filteredDistricts = this.districtLookup.filter(d => d.value.toLowerCase().includes(search));
    }
  }

  onDistrictSelectOpened(opened: boolean): void {
    if (opened) {
      this.districtFilterCtrl.setValue('');
      this.filteredDistricts = [...this.districtLookup];
    }
  }

  filterSchools(): void {
    const search = this.schoolFilterCtrl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredSchools = [...this.schoolLookup];
    } else {
      this.filteredSchools = this.schoolLookup.filter(s => s.value.toLowerCase().includes(search));
    }
  }

  onSchoolSelectOpened(opened: boolean): void {
    if (opened) {
      this.schoolFilterCtrl.setValue('');
      this.filteredSchools = [...this.schoolLookup];
    }
  }

  onVerifiableTeachingStateChange(): void {
    const stateKey = this.verifiableTeachingExperienceForm.get('state')?.value;
    const selectedState = this.verifiableTeachingStatesList.find(s => s.key === stateKey);
    this.isVerifiableTeachingNevada = selectedState?.value?.toLowerCase() === 'nevada';
    if (!this.isVerifiableTeachingNevada) {
      this.verifiableTeachingExperienceForm.get('district')?.reset();
      this.verifiableTeachingExperienceForm.get('district')?.clearValidators();
      this.verifiableTeachingExperienceForm.get('school')?.reset();
      this.verifiableTeachingExperienceForm.get('school')?.clearValidators();
      this.verifiableTeachingExperienceForm.get('school_name')?.reset();
      this.verifiableTeachingExperienceForm.get('school_name')?.clearValidators();
      this.showVerifiableTeachingSchoolName = false;
    } else {
      this.verifiableTeachingExperienceForm.get('district')?.setValidators(Validators.required);
      this.verifiableTeachingExperienceForm.get('school')?.setValidators(Validators.required);
    }
    this.verifiableTeachingExperienceForm.get('district')?.updateValueAndValidity();
    this.verifiableTeachingExperienceForm.get('school')?.updateValueAndValidity();
    this.verifiableTeachingExperienceForm.get('school_name')?.updateValueAndValidity();
  }

  onVerifiableTeachingSchoolChange(): void {
    const schoolKey = this.verifiableTeachingExperienceForm.get('school')?.value;
    const selectedSchool = this.schoolLookup.find(s => s.key === schoolKey);
    const schoolValue = selectedSchool?.value?.toLowerCase() || '';
    this.showVerifiableTeachingSchoolName = schoolValue === 'other charter school' || schoolValue === 'other private school';
    if (this.showVerifiableTeachingSchoolName) {
      this.verifiableTeachingExperienceForm.get('school_name')?.setValidators([
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern(/^[A-Za-z\s.,\-&'()/]{2,60}$/)
      ]);
    } else {
      this.verifiableTeachingExperienceForm.get('school_name')?.clearValidators();
      this.verifiableTeachingExperienceForm.get('school_name')?.reset();
    }
    this.verifiableTeachingExperienceForm.get('school_name')?.updateValueAndValidity();
  }

  calculateVerifiableTeachingTotalYears(): void {
    const fromVal = this.verifiableTeachingExperienceForm.get('from')?.value;
    const toVal = this.verifiableTeachingExperienceForm.get('to')?.value;
    if (fromVal && toVal && fromVal.length === 10 && toVal.length === 10) {
      const [fm, fd, fy] = fromVal.split('/').map(Number);
      const [tm, td, ty] = toVal.split('/').map(Number);
      const fromDate = new Date(fy, fm - 1, fd);
      const toDate = new Date(ty, tm - 1, td);
      if (toDate >= fromDate) {
        const diffMs = toDate.getTime() - fromDate.getTime();
        const totalYears = (diffMs / (1000 * 60 * 60 * 24 * 365.25)).toFixed(2);
        this.verifiableTeachingExperienceForm.get('total_years')?.setValue(totalYears);
      }
    }
  }

  openVerifiableTeachingExperienceForm(row?: any): void {
    this.verifiableTeachingExperienceForm.reset();
    this.isVerifiableTeachingNevada = false;
    this.showVerifiableTeachingSchoolName = false;
    this.showVerifiableTeachingExperienceForm = true;
    this.isEditVerifiableTeachingExperience = !!row;
    this.selectedVerifiableTeachingExperience = row ? { ...row } : null;
    if (row) {
      const stateKey = this.keyByValue(this.verifiableTeachingStatesList, row?.state);
      const districtKey = this.keyByValue(this.districtLookup, row?.district);
      const schoolKey = this.keyByValue(this.schoolLookup, row?.school);
      const statusKey = this.keyByValue(this.verifiableTeachingStatusLookup, row?.status);
      const fromDate = this.formatDateForInput(row?.from || row?.from_date_of_service || row?.from_date || row?.start_date);
      const toDate = this.formatDateForInput(row?.to || row?.to_date_of_service || row?.to_date || row?.end_date);
      this.verifiableTeachingExperienceForm.patchValue({
        state: stateKey,
        district: districtKey,
        school: schoolKey,
        school_name: row?.school_name,
        from: fromDate,
        to: toDate,
        total_years: row?.total_years,
        status: statusKey
      });
      this.onVerifiableTeachingStateChange();
      this.onVerifiableTeachingSchoolChange();
    }
  }

  saveVerifiableTeachingExperience(): void {
    if (this.verifiableTeachingExperienceForm.invalid) {
      this.verifiableTeachingExperienceForm.markAllAsTouched();
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    if (this.isVerifiableTeachingDuplicate()) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }

    const formValue = { ...this.verifiableTeachingExperienceForm.value };
    if (formValue.total_years !== undefined && formValue.total_years !== null) {
      formValue.total_years = Number(formValue.total_years);
    }
    const payload = {
      ...formValue,
      person_id: this.personId,
      updated_by: this.getCurrentUserId(),
      createdDate: new Date().toISOString()
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Insert_Verifiable_teaching_experience, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showVerifiableTeachingExperienceForm = false;
          this.verifiableTeachingExperienceForm.reset();
          this.isVerifiableTeachingNevada = false;
          this.showVerifiableTeachingSchoolName = false;
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to save, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to save, Please try after sometime!')
    });
  }

  updateVerifiableTeachingExperience(): void {
    if (this.verifiableTeachingExperienceForm.invalid) {
      this.verifiableTeachingExperienceForm.markAllAsTouched();
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    const currentVtId = this.selectedVerifiableTeachingExperience?.profile_teaching_experience_id;
    const fvVt = this.verifiableTeachingExperienceForm.value;
    const nVt = (val: unknown): string => {
      if (val === null || val === undefined || val === '---') return '';
      return String(val).toLowerCase().trim().replace(/\s+/g, ' ');
    };
    const isDuplicateVt = this.verifiableTeachingExpRows.some((row: any) =>
      String(row['profile_teaching_experience_id']) !== String(currentVtId) &&
      nVt(this.keyByValue(this.verifiableTeachingStatesList, row['state'])) === nVt(fvVt.state) &&
      nVt(this.formatDateForInput(row['from'] || row['from_date_of_service'] || row['from_date'] || row['start_date'])) === nVt(fvVt.from) &&
      nVt(this.formatDateForInput(row['to'] || row['to_date_of_service'] || row['to_date'] || row['end_date'])) === nVt(fvVt.to) &&
      nVt(this.keyByValue(this.verifiableTeachingStatusLookup, row['status'])) === nVt(fvVt.status) &&
      nVt(this.keyByValue(this.districtLookup, row['district'])) === nVt(fvVt.district) &&
      nVt(this.keyByValue(this.schoolLookup, row['school'])) === nVt(fvVt.school)
    );
    if (isDuplicateVt) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }

    const formValue = { ...this.verifiableTeachingExperienceForm.value };
    if (formValue.total_years !== undefined && formValue.total_years !== null) {
      formValue.total_years = Number(formValue.total_years);
    }
    const payload = {
      ...formValue,
      profile_teaching_experience_id: this.selectedVerifiableTeachingExperience?.profile_teaching_experience_id,
      person_id: this.personId,
      updated_by: this.getCurrentUserId()
    };

    this.httpService.put(API_ENDPOINTS.USER_EXPERIENCE_TAB.Update_Verifiable_teaching_experience, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showVerifiableTeachingExperienceForm = false;
          this.isEditVerifiableTeachingExperience = false;
          this.dialogRef?.close();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to update, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to update, Please try after sometime!')
    });
  }

  resetVerifiableTeachingExperienceForm(): void {
    const row = this.selectedVerifiableTeachingExperience ? this.selectedVerifiableTeachingExperience : null;
    if (row) {
      const stateKey = this.keyByValue(this.verifiableTeachingStatesList, row?.state);
      const districtKey = this.keyByValue(this.districtLookup, row?.district);
      const schoolKey = this.keyByValue(this.schoolLookup, row?.school);
      const statusKey = this.keyByValue(this.verifiableTeachingStatusLookup, row?.status);
      const from = this.formatDateForInput(row?.from || row?.from_date_of_service || row?.from_date || row?.start_date);
      const to = this.formatDateForInput(row?.to || row?.to_date_of_service || row?.to_date || row?.end_date);
      this.verifiableTeachingExperienceForm.patchValue({
        state: stateKey,
        district: districtKey,
        school: schoolKey,
        school_name: row?.school_name,
        from: from,
        to: to,
        total_years: row?.total_years,
        status: statusKey
      });
      this.onVerifiableTeachingStateChange();
      this.onVerifiableTeachingSchoolChange();
    } else {
      this.verifiableTeachingExperienceForm.reset();
      this.isVerifiableTeachingNevada = false;
      this.showVerifiableTeachingSchoolName = false;
    }
  }

  cancelVerifiableTeachingExperienceForm(): void {
    if (this.isEditVerifiableTeachingExperience) {
      this.dialogRef?.close();
    }
    this.verifiableTeachingExperienceForm.markAsUntouched();
    this.verifiableTeachingExperienceForm.markAsPristine();
    this.showVerifiableTeachingExperienceForm = false;
    this.isEditVerifiableTeachingExperience = false;
    this.isVerifiableTeachingNevada = false;
    this.showVerifiableTeachingSchoolName = false;
    this.verifiableTeachingExperienceForm.reset();
  }

  openVerifiableTeachingExperienceModal(row: any): void {
    this.isEditVerifiableTeachingExperience = true;
    this.selectedVerifiableTeachingExperience = { ...row };
    const stateKey = this.keyByValue(this.verifiableTeachingStatesList, row?.state);
    const districtKey = this.keyByValue(this.districtLookup, row?.district);
    const schoolKey = this.keyByValue(this.schoolLookup, row?.school);
    const statusKey = this.keyByValue(this.verifiableTeachingStatusLookup, row?.status);
    const fromDate = this.formatDateForInput(row?.from || row?.from_date_of_service || row?.from_date || row?.start_date);
    const toDate = this.formatDateForInput(row?.to || row?.to_date_of_service || row?.to_date || row?.end_date);
    this.verifiableTeachingExperienceForm.patchValue({
      state: stateKey,
      district: districtKey,
      school: schoolKey,
      school_name: row?.school_name,
      from: fromDate,
      to: toDate,
      total_years: row?.total_years,
      status: statusKey
    });
    this.onVerifiableTeachingStateChange();
    this.onVerifiableTeachingSchoolChange();
    this.dialogRef = this.modalService.open(this.verifiableTeachingExpEditModal, { centered: true, size: 'xl', backdrop: 'static', windowClass: 'custom-edit-record-modal' });
    this.dialogRef.result.finally(() => {
      this.isEditVerifiableTeachingExperience = false;
      this.verifiableTeachingExperienceForm.reset();
      this.isVerifiableTeachingNevada = false;
      this.showVerifiableTeachingSchoolName = false;
    });
  }

  openVerifiableTeachingExperienceDeleteModal(row: any): void {
    this.verifiableTeachingExperienceDeleteRow = row;
    this.openDeleteConfirmation('verifiable teaching experience record', () => this.confirmDeleteVerifiableTeachingExperience());
  }

  confirmDeleteVerifiableTeachingExperience(): void {
    const row = this.verifiableTeachingExperienceDeleteRow;
    if (!row?.profile_teaching_experience_id || !this.personId) {
      this.alertService.error('Missing profile_teaching_experience_id or person_id.');
      this.verifiableTeachingExperienceDeleteRow = null;
      return;
    }
    const payload = {
      profile_teaching_experience_id: row.profile_teaching_experience_id,
      person_id: this.personId
    };
    this.httpService.patch(API_ENDPOINTS.USER_EXPERIENCE_TAB.Delete_Verifiable_teaching_experience, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.alertService.success(this.toastMessage['T0040'] || 'Deleted successfully.');
          this.refreshExperienceData();
          this.cd.detectChanges();
          this.verifiableTeachingExperienceDeleteRow = null;
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to delete verifiable teaching experience. Please try again.');
          this.verifiableTeachingExperienceDeleteRow = null;
        }
      },
      error: (err: any) => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete verifiable teaching experience. Please try again.');
        this.verifiableTeachingExperienceDeleteRow = null;
      }
    });
  }

  // --- Verifiable Business and Industry Work Experience CRUD ---

  openBusinessIndustryForm(row?: any): void {
    this.businessIndustryForm.reset();
    this.showBusinessIndustryForm = true;
    this.isEditBusinessIndustry = !!row;
    this.selectedBusinessIndustry = row ? { ...row } : null;
    if (row) {
      this.businessIndustryForm.patchValue({
        employer_name: row?.employer_name
      });
    }
  }

  saveBusinessIndustry(): void {
    if (this.businessIndustryForm.invalid) {
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    if (this.isBusinessIndustryDuplicate()) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    const payload = {
      ...this.businessIndustryForm.value,
      person_id: this.personId,
      updated_by: this.getCurrentUserId(),
      createdDate: new Date().toISOString(),
      created_by: this.getCurrentUserId()
    };
    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Insert_Business_industry, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showBusinessIndustryForm = false;
          this.businessIndustryForm.reset();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to save, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to save, Please try after sometime!')
    });
  }

  updateBusinessIndustry(): void {
    if (this.businessIndustryForm.invalid) {
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }
    const currentBiId = this.selectedBusinessIndustry?.profile_business_industry_id;
    const fvBi = this.businessIndustryForm.value;
    const isDuplicateBi = this.businessIndustryRows.some((row: any) =>
      String(row['profile_business_industry_id']) !== String(currentBiId) &&
      this.normalizeStr(row['employer_name']) === this.normalizeStr(fvBi.employer_name)
    );
    if (isDuplicateBi) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    const payload = {
      ...this.businessIndustryForm.value,
      profile_business_industry_id: this.selectedBusinessIndustry?.profile_business_industry_id,
      person_id: this.personId,
      updated_by: this.getCurrentUserId()
    };
    this.httpService.put(API_ENDPOINTS.USER_EXPERIENCE_TAB.Update_Business_industry, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showBusinessIndustryForm = false;
          this.isEditBusinessIndustry = false;
          this.dialogRef?.close();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to update, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to update, Please try after sometime!')
    });
  }

  cancelBusinessIndustryForm(): void {
    if (this.isEditBusinessIndustry) {
      this.dialogRef?.close();
    }
    this.businessIndustryForm.markAsUntouched();
    this.businessIndustryForm.markAsPristine();
    this.showBusinessIndustryForm = false;
    this.isEditBusinessIndustry = false;
    this.businessIndustryForm.reset();
  }

  resetBusinessIndustryForm(): void {
    const row = this.selectedBusinessIndustry;
    if (row) {
      this.businessIndustryForm.patchValue({
        employer_name: row?.employer_name
      });
    } else {
      this.businessIndustryForm.reset();
    }
  }

  openBusinessIndustryModal(row: any): void {
    this.isEditBusinessIndustry = true;
    this.selectedBusinessIndustry = { ...row };
    this.businessIndustryForm.patchValue({
      employer_name: row?.employer_name
    });
    this.dialogRef = this.modalService.open(this.businessIndustryEditModal, { centered:true, size: 'xl', backdrop: 'static', windowClass: 'custom-edit-record-modal' });
    this.dialogRef.result.finally(() => {
      this.isEditBusinessIndustry = false;
      this.businessIndustryForm.reset();
    });
  }

  openBusinessIndustryDeleteModal(row: any): void {
    this.businessIndustryDeleteRow = row;
    this.openDeleteConfirmation('business and industry work experience record', () => this.confirmDeleteBusinessIndustry());
  }

  confirmDeleteBusinessIndustry(): void {
    const row = this.businessIndustryDeleteRow;
    if (!row?.profile_business_industry_id || !this.personId) {
      this.alertService.error('Missing profile_business_industry_id or person_id.');
      this.businessIndustryDeleteRow = null;
      return;
    }
    const payload = {
      profile_business_industry_id: row.profile_business_industry_id,
      person_id: this.personId
    };
    this.httpService.patch(API_ENDPOINTS.USER_EXPERIENCE_TAB.Delete_Business_industry, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.alertService.success(this.toastMessage['T0040'] || 'Deleted successfully.');
          this.refreshExperienceData();
          this.cd.detectChanges();
          this.businessIndustryDeleteRow = null;
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to delete business and industry work experience. Please try again.');
          this.businessIndustryDeleteRow = null;
        }
      },
      error: (err: any) => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete business and industry work experience. Please try again.');
        this.businessIndustryDeleteRow = null;
      }
    });
  }

  openOutOfStateLicenseForm(row?: any): void {
    this.showOutOfStateLicenseForm = true;
    this.isEditOutOfStateLicense = !!row;
    if (row) {
      const stateKey = this.keyByValue(this.statesList, row?.state);
      this.outOfStateLicenseForm.patchValue({
        state: stateKey,
        license_number: row?.license_number,
        license_status: row?.license_status
      });
    }
  }
  private loaderrorMessages(): void {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get(API_ENDPOINTS.LOOKUP_DOCUMENT_CATEGORY),
      this.httpService.get(API_ENDPOINTS.LOOKUP_DOCUMENT_TYPE),
      this.httpService.get('/domain/lookup?requestType=DISTRICT'),
      this.httpService.get('/domain/lookup?requestType=SCHOOLS'),
      this.httpService.get('/domain/lookup?requestType=VERIFIABLE_TEACHING_STATUS'),
      this.httpService.get('/domain/lookup?requestType=STATE')
    ]).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: ((items: any[]) => {
        this.errorMessages = {};
        const list = (items[0]?.data || []);
        list.forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.errorMessages[msg.key] = msg.value;
        });

        this.placeholderMessage = {};
        const placeholderList = (items[1]?.data || []);
        placeholderList.forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.placeholderMessage[msg.key] = msg.value;
        });

        this.toastMessage = {};
        const toastList = (items[2]?.data || []);
        toastList.forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.toastMessage[msg.key] = msg.value;
        });

        this.genericMessage = {};
        const genericList = (items[3]?.data || []);
        genericList.forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.genericMessage[msg.key] = msg.value;
        });
        const mapLookupFull = (arr: any[]): Array<{ id: number; parentid: number | null; key: string; value: string }> =>
          (arr || []).map((x: any) => ({
            id: Number(x?.id ?? 0),
            parentid: x?.parentid != null ? Number(x.parentid) : null,
            key: String(x?.key ?? ''),
            value: String(x?.value ?? '')
          }));

        this.docCategoryOptions = mapLookupFull(items[4]?.data || []).map((x: any) => ({
          id: x.id,
          key: x.key,
          value: x.value
        }));
        this.defaultDocCategoryOptions = [...this.docCategoryOptions];
        this.allDocTypeOptions = mapLookupFull(items[5]?.data || []);
        this.uploadFilteredDocTypeOptions = [...this.allDocTypeOptions];

        // DISTRICT lookup
        this.districtLookup = (items[6]?.data || [])
          .filter((item: any) => item?.key !== undefined && item?.value !== undefined)
          .map((item: any) => ({ key: String(item.key), value: String(item.value) }))
          .sort((a: any, b: any) => a.value.localeCompare(b.value));
        this.filteredDistricts = [...this.districtLookup];
        this.districtFilterCtrl.valueChanges.subscribe(() => {
          this.filterDistricts();
        });

        // SCHOOL lookup
        this.schoolLookup = (items[7]?.data || [])
          .filter((item: any) => item?.key !== undefined && item?.value !== undefined)
          .map((item: any) => ({ key: String(item.key), value: String(item.value) }))
          .sort((a: any, b: any) => a.value.localeCompare(b.value));
        this.filteredSchools = [...this.schoolLookup];
        this.schoolFilterCtrl.valueChanges.subscribe(() => {
          this.filterSchools();
        });

        // VERIFIABLE TEACHING STATUS lookup
        this.verifiableTeachingStatusLookup = (items[8]?.data || [])
          .filter((item: any) => item?.key !== undefined && item?.value !== undefined)
          .map((item: any) => ({ key: String(item.key), value: String(item.value) }))
          .sort((a: any, b: any) => a.value.localeCompare(b.value));

        // STATE lookup for verifiable teaching experience
        this.verifiableTeachingStatesList = (items[9]?.data || [])
          .filter((item: any) => item?.key !== undefined && item?.value !== undefined)
          .map((item: any) => ({ key: String(item.key), value: String(item.value) }))
          .sort((a: any, b: any) => a.value.localeCompare(b.value));
        this.filteredVerifiableTeachingExpStates = [...this.verifiableTeachingStatesList];
        this.verifiableTeachingExpStateFilterCtrl.valueChanges.subscribe(() => {
          this.filterVerifiableTeachingExpStates();
        });
      }),
      error: () => {
        // silent fail — message lookups not critical
      }
    });
  }

  openOutOfStateLicenseModal(row: any): void {
    this.isEditOutOfStateLicense = true;
    this.selectedOutOfStateLicense = { ...row };
    const stateKey = this.keyByValue(this.statesList, row?.state);
    this.outOfStateLicenseForm.patchValue({
      state: stateKey,
      license_number: row?.license_number,
      license_status: row?.license_status
    });
    this.dialogRef = this.modalService.open(this.outOfStateLicenseEditModal, { centered: true, size: 'xl', backdrop: 'static', windowClass: 'custom-edit-record-modal' });

    this.dialogRef.closed.subscribe(() => {
      this.outOfStateLicenseForm.reset();
    });

    this.dialogRef.dismissed.subscribe(() => {
      this.outOfStateLicenseForm.reset();
    });

  }

  saveOutOfStateLicense(): void {
    if (this.outOfStateLicenseForm.invalid) {
      this.outOfStateLicenseForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }

    if (this.isOutOfStateLicenseDuplicate()) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }

    // Map status string to key
    const formValue = { ...this.outOfStateLicenseForm.value };

    const payload = {
      ...formValue,
      person_id: this.personId ? Number(this.personId) : null,
      updated_by: this.getCurrentUserId(),
      createdDate: new Date().toISOString()
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Insert_Out_of_state, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showOutOfStateLicenseForm = false;
          this.outOfStateLicenseForm.reset();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to save, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to save, Please try after sometime!')
    });
  }

  updateOutOfStateLicense(): void {
    if (this.outOfStateLicenseForm.invalid) {
      this.outOfStateLicenseForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }

    const currentOslId = this.selectedOutOfStateLicense?.profile_out_of_state_license_id;
    const fvOsl = this.outOfStateLicenseForm.value;
    const isDuplicateOsl = this.outOfStateLicRows.some((row: any) =>
      String(row['profile_out_of_state_license_id']) !== String(currentOslId) &&
      this.normalizeStr(this.keyByValue(this.statesList, row['state'])) === this.normalizeStr(fvOsl.state) &&
      this.normalizeStr(row['license_number']) === this.normalizeStr(fvOsl.license_number) &&
      this.normalizeStr(row['license_status']) === this.normalizeStr(fvOsl.license_status)
    );
    if (isDuplicateOsl) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }

    // Map status string to key
    const formValue = { ...this.outOfStateLicenseForm.value };

    // For update, include person_id and license_number (from form or last edited row)
    const payload = {
      ...formValue,
      person_id: this.personId ? Number(this.personId) : null,
      updated_by: this.getCurrentUserId(),
      profile_out_of_state_license_id: this.selectedOutOfStateLicense?.profile_out_of_state_license_id
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Update_Out_of_state, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showOutOfStateLicenseForm = false;
          this.isEditOutOfStateLicense = false;
          this.outOfStateLicenseForm.reset();
          this.dialogRef?.close();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to update, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to update, Please try after sometime!')
    });
  }

  resetOutOfStateLicenseForm(): void {
    const row = this.selectedOutOfStateLicense ? this.selectedOutOfStateLicense : null;
    if (row) {
      const stateKey = this.keyByValue(this.statesList, row?.state);
      this.outOfStateLicenseForm.patchValue({
        state: stateKey,
        license_number: row?.license_number,
        license_status: row?.license_status
      });
    } else {
      this.outOfStateLicenseForm.reset();
    }
  }

  cancelOutOfStateLicenseForm(): void {
    this.outOfStateLicenseForm.markAsUntouched();
    this.outOfStateLicenseForm.markAsPristine();
    this.showOutOfStateLicenseForm = false;
    this.outOfStateLicenseForm.reset();
  }

  selectedGridForUpload: 'out-of-state-lic_notes' | 'teaching-experience' | 'assignment-experience' | 'employment-support' | 'business-industry-support' | 'verifiable-teaching-experience' = 'out-of-state-lic_notes';

  onSupportingDocumentsUpload(key: 'out-of-state-lic_notes' | 'teaching-experience' | 'assignment-experience' | 'employment-support' | 'business-industry-support' | 'verifiable-teaching-experience'): void {
    if (!this.personId) {
      this.alertService.warn('Person ID is required.');
      return;
    }
    this.selectedGridForUpload = key;
    this.staffDocNoticeModal.open({ contentVariant: 'default' }).then((confirmed) => {
      if (confirmed) {
        this.openUploadModalAfterNotice();
      }
    });
  }

  private openUploadModalAfterNotice(): void {
    this.currentUploadSection = this.selectedGridForUpload;
    this.uploadData = { file: null, fileName: '', type: '' };
    this.uploadError = null;
    this.saveEnable = true;
    this.uploadDocCategoryOptions = [];
    this.uploadFilteredDocTypeOptions = [];
    this.uploadDocMetaForm.reset({ category: '', type: '' });
    this.uploadModalRef = this.modalService.open(this.uploadModel, { windowClass: 'lookup-experience-doc-modal', centered: true, backdrop: 'static', keyboard: false });
    this.supportingDocLookupService
      .getAllTopLevelCategories()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((cats: SupportingDocumentGridOption[]) => {
        this.uploadDocCategoryOptions = cats;
        this.configureUploadOptionsForSection();
      });
  }

  private configureUploadOptionsForSection(): void {
    const sectionCategoryValueMap: Record<string, string> = {
      'out-of-state-lic_notes': 'Out of State or Country Licensure',
      'verifiable-teaching-experience': 'Verifiable Teaching Experience',
      'business-industry-support': 'Verifiable Business and Industry Work Experience',
      'employment-support': 'Employment'
    };
    const fixedCategoryValue = sectionCategoryValueMap[this.currentUploadSection];
    if (fixedCategoryValue) {
      const cat = this.uploadDocCategoryOptions.find(c =>
        c.value === fixedCategoryValue ||
        c.key === fixedCategoryValue ||
        String(c.optionaldata?.section_name ?? '').trim() === fixedCategoryValue
      );
      if (cat) {
        this.uploadCategoryLabel = cat.value;
        this.uploadDocMetaForm.patchValue({ category: cat.key });
      } else {
        this.uploadCategoryLabel = fixedCategoryValue;
        this.uploadDocMetaForm.patchValue({ category: fixedCategoryValue });
      }
      // Populate type options from grid rows (same pattern as activities tab)
      const gridTypeOptions = this.getGridTypeOptionsForSection();
      this.uploadFilteredDocTypeOptions = gridTypeOptions.map(o => ({ key: o.key, value: o.value }));
      this.uploadDocMetaForm.get('category')?.disable();
    } else {
      this.uploadCategoryLabel = '';
      this.uploadDocMetaForm.get('category')?.enable();
    }
  }

  private getGridTypeOptionsForSection(): { key: string; value: string }[] {
    switch (this.currentUploadSection) {
      case 'out-of-state-lic_notes':
        return this.getOutOfStateTypeOptions();
      case 'verifiable-teaching-experience':
        return this.getVerifiableTeachingTypeOptions();
      case 'business-industry-support':
        return this.getBusinessIndustryTypeOptions();
      case 'employment-support':
        return this.getEmploymentTypeOptions();
      default:
        return [];
    }
  }

  private getOutOfStateTypeOptions(): { id: number; parentid: number | null; key: string; value: string }[] {
    const states = (this.outOfStateLicRows || [])
      .map((row: any) => String(row?.state ?? '').trim())
      .filter(s => s !== '');
    const unique = [...new Set(states)];
    return unique.map((s, index) => ({
      id: index + 1,
      parentid: null,
      key: s,
      value: s
    }));
  }

  private getVerifiableTeachingTypeOptions(): { id: number; parentid: number | null; key: string; value: string }[] {
    const states = (this.verifiableTeachingExpRows || [])
      .map((row: any) => String(row?.state ?? '').trim())
      .filter(s => s !== '');
    const unique = [...new Set(states)];
    return unique.map((s, index) => ({
      id: index + 1,
      parentid: null,
      key: s,
      value: s
    }));
  }

  private getBusinessIndustryTypeOptions(): { id: number; parentid: number | null; key: string; value: string }[] {
    const names = (this.businessIndustryRows || [])
      .map((row: any) => String(row?.employer_name ?? '').trim())
      .filter(n => n !== '');
    const unique = [...new Set(names)];
    return unique.map((n, index) => ({
      id: index + 1,
      parentid: null,
      key: n,
      value: n
    }));
  }

  private getEmploymentTypeOptions(): { id: number; parentid: number | null; key: string; value: string }[] {
    const names = (this.employmentRows || [])
      .map((row: any) => String(row?.employer_name ?? '').trim())
      .filter(n => n !== '');
    const unique = [...new Set(names)];
    return unique.map((n, index) => ({
      id: index + 1,
      parentid: null,
      key: n,
      value: n
    }));
  }

  onUploadTypeDropdownShow(dropdown: Dropdown): void {
    flipLookupUploadDropdownAbove(dropdown);
  }

  closeUploadPopup(): void {
     // Only check user-entered values: file and type (category is often pre-populated and disabled)
    const typeVal = this.uploadDocMetaForm.get('type')?.value;
    const categoryEnabled = this.uploadDocMetaForm.get('category')?.enabled;
    const categoryVal = categoryEnabled ? this.uploadDocMetaForm.get('category')?.value : null;

    const hasDraft = !!(
      this.uploadData?.file ||
      (typeVal != null && String(typeVal).trim() !== '') ||
      (categoryVal != null && String(categoryVal).trim() !== '')
    );

    if (hasDraft) {
      const modalRef = this.modalService.open(ModalComponent, {
        size: 'md',
        centered: true,
        backdrop: 'static'
      });
      modalRef.componentInstance.modalConfig = getCancelFileUploadModalConfig();
      modalRef.componentInstance.emitData.pipe(take(1)).subscribe((result: any) => {
        modalRef.close();
        if (result?.code === 'Y' || result?.confirmed === true) {
          this._actuallyCloseUploadPopup();
        }
      });
    } else {
      this._actuallyCloseUploadPopup();
    }
  }

  private _actuallyCloseUploadPopup(): void {
    this.uploadModalRef?.close();
    this.uploadModalRef = null;
    this.uploadData = { file: null, fileName: '', type: '' };
    this.uploadError = null;
    this.saveEnable = true;
    this.uploadDocMetaForm.reset({ category: '', type: '' });
    this.uploadDocCategoryOptions = [];
    this.uploadFilteredDocTypeOptions = [];
    this.uploadCategoryLabel = '';
  }

  onUploadDocCategoryChange(): void {
    const key = this.uploadDocMetaForm.get('category')?.value;
    this.uploadDocMetaForm.patchValue({ type: '' });
    if (!key) {
      this.uploadFilteredDocTypeOptions = [];
      return;
    }
    const selectedCat = this.uploadDocCategoryOptions.find(c => c.key === key);
    if (!selectedCat) {
      this.uploadFilteredDocTypeOptions = [];
      return;
    }
    this.supportingDocLookupService
      .getTypesByCategory(selectedCat)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types: SupportingDocumentGridOption[]) => {
        this.uploadFilteredDocTypeOptions = types;
      });
  }

  clearFileInput(event: any): void {
    event.target.value = null;
  }

  onDropFile(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.setUploadedFile(event.dataTransfer.files[0]);
    }
  }

  handleFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    this.setUploadedFile(input.files[0]);
  }

  private setUploadedFile(file: File): void {
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      this.uploadError = this.errorMessages['M0129'];
      this.uploadData = { file: null, fileName: '', type: '' };
      this.saveEnable = true;
      return;
    }
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = this.errorMessages['M0128'];
      this.uploadData = { file: null, fileName: '', type: '' };
      this.saveEnable = true;
      return;
    }

    this.uploadData.file = file;
    this.uploadData.fileName = file.name;
    this.uploadData.type = file.type || '';
    this.uploadError = null;
    this.saveEnable = false;
  }

  private getCurrentUserId(): number | null {
    try {
      const userRaw = sessionStorage.getItem('user');
      if (!userRaw) return null;
      const user = JSON.parse(userRaw);
      const id = user?.user_id ?? user?.id ?? user?.userId ?? null;
      return id !== null ? Number(id) : null;
    } catch {
      return null;
    }
  }

  async onSaveUploadFile(): Promise<void> {
    if (!this.uploadData?.fileName) {
      this.uploadError = 'Please select a file.';
      return;
    }
    if (this.uploadDocMetaForm.invalid) {
      this.uploadDocMetaForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    this.selectedUploadMeta = {
      fileName: this.uploadData.fileName || '',
      contentType: this.uploadData.type || ''
    };

    let formData = {};
    if (this.currentUploadSection === 'teaching-experience') {
      formData = this.teachingExperienceForm.value;
    } else if (this.currentUploadSection === 'assignment-experience') {
      formData = this.assignmentExperienceForm.value;
    } else if (this.currentUploadSection === 'employment-support') {
      formData = this.employmentForm.value;
    } else if (this.currentUploadSection === 'business-industry-support') {
      formData = this.businessIndustryForm.value;
    } else {
      formData = this.outOfStateLicenseForm.value;
    }

    const selectedCategoryKey = String(this.uploadDocMetaForm.get('category')?.value ?? '').trim();
    const selectedTypeKey = String(this.uploadDocMetaForm.get('type')?.value ?? '').trim();
    const categoryMatch = this.uploadDocCategoryOptions.find(c => c.key === selectedCategoryKey);
    const typeMatch = (this.uploadFilteredDocTypeOptions || []).find(t => t.key === selectedTypeKey);
    // If found in lookup options → send key; otherwise send the form value as-is
    const categoryPayload = categoryMatch ? categoryMatch.key : selectedCategoryKey;
    const typePayload = typeMatch ? typeMatch.key : selectedTypeKey;
    // Keep display values for duplicate guard / local cache
    const selectedCategoryValue = (categoryMatch?.value && String(categoryMatch.value).trim()) || selectedCategoryKey;
    const selectedTypeValue = (typeMatch?.value && String(typeMatch.value).trim()) || selectedTypeKey;

    const file = this.uploadData.file as File;
    if (!(file instanceof File)) {
      this.alertService.error('Please select a file.');
      return;
    }
    const personIdNum = this.personId ? Number(this.personId) : 0;
    const categoryLookups = toDuplicateLookupOptions(this.uploadDocCategoryOptions);
    const typeLookups = toDuplicateLookupOptions(this.uploadFilteredDocTypeOptions);
    if (personIdNum && (categoryPayload || selectedCategoryValue)) {
      const proceed = await this.duplicateDocGuard.ensureProceedOrCancel({
        educatorDashboardRow: this.educatorDashboardRow as Record<string, unknown> | null,
        categoryKey: categoryPayload || selectedCategoryValue,
        typeKey: typePayload || selectedTypeValue || undefined,
        fileName: file.name,
        screenName: 'Lookup User - Experience',
        personId: personIdNum,
        categoryDisplay: selectedCategoryValue,
        typeDisplay: selectedTypeValue || undefined,
        categoryLookups,
        typeLookups
      });
      if (!proceed) {
        this.uploadData = { file: null, fileName: '', type: '' };
        this.uploadError = '';
        this.selectedUploadMeta = { fileName: '', contentType: '' };
        return;
      }
    }

    const sectionTabLabelMap: Record<string, string> = {
      'out-of-state-lic_notes': 'Out of State or Country Licensure',
      'verifiable-teaching-experience': 'Verifiable Teaching Experience',
      'business-industry-support': 'Verifiable Business and Industry Work Experience',
      'employment-support': 'Employment'
    };
    const profileTabSection = sectionTabLabelMap[this.currentUploadSection] || this.currentUploadSection;

    const uploadData = {
      person_id: this.personId ? Number(this.personId) : null,
      uploaded_by: this.getCurrentUserId(),
      uploaded_at: new Date().toISOString(),
      profile_tab_section: profileTabSection,
      file_name: this.selectedUploadMeta.fileName || '',
      cd_document_category: categoryPayload || null,
      cd_document_type: typePayload || null,
      formid: LOOKUP_USER_SECTION_FORM_IDS[profileTabSection] ?? null,
      form_data: {
        ...formData,
        cd_document_category: categoryPayload || null,
        cd_document_type: typePayload || null
      }
    };

    educatorDocumentUploadWithClientBlobPut(this.httpService, file, uploadData)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          appendProfileUploadToDashboardRow(
            this.educatorDashboardRow as Record<string, unknown> | null,
            buildProfileUploadRowForLocalCache({
              cd_document_category: categoryPayload || '',
              cd_document_type: typePayload || '',
              document_category: selectedCategoryValue,
              document_type: selectedTypeValue || undefined,
              file_name: file.name,
              profile_tab_section: profileTabSection
            })
          );
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
          this._actuallyCloseUploadPopup();
          this.refreshExperienceData();
        },
        error: () => {
          this.alertService.error('Failed to upload document. Please try again.');
        }
      });
  }

  openAssignmentExperienceForm(row?: any): void {
    this.showAssignmentExperienceForm = true;
    this.isEditAssignmentExperience = !!row;
    if (row) {
      this.assignmentExperienceForm.patchValue({
        district: row?.district,
        school: row?.school,
        assignment: row?.assignment,
        school_year: row?.school_year,
        hours: row?.hours
      });
    }
  }

  saveAssignmentExperience(): void {
    if (this.assignmentExperienceForm.invalid) {
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    const payload = {
      ...this.assignmentExperienceForm.value,
      person_id: this.personId,
      updated_by: this.getCurrentUserId(),
      createdDate: new Date().toISOString()
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Insert_Assignment_experience, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showAssignmentExperienceForm = false;
          this.assignmentExperienceForm.reset();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to save, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to save, Please try after sometime!')
    });
  }

  updateAssignmentExperience(): void {
    if (this.assignmentExperienceForm.invalid) {
      window.alert('Please fill in all required fields');
      return;
    }

    // For update, include person_id and assignment_experience_id
    const payload = {
      ...this.assignmentExperienceForm.value,
      person_id: this.personId,
      assignment_experience_id: this.assignmentExperienceForm.value.assignment_experience_id,
      updated_by: this.getCurrentUserId()
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Update_Assignment_experience, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showAssignmentExperienceForm = false;
          this.isEditAssignmentExperience = false;
          this.dialogRef?.close();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to update, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to update, Please try after sometime!')
    });
  }

  resetAssignmentExperienceForm(): void {
    this.assignmentExperienceForm.reset();
  }

  cancelAssignmentExperienceForm(): void {
    if (this.isEditAssignmentExperience) {
      this.dialogRef?.close();
    }
    this.assignmentExperienceForm.markAsUntouched();
    this.assignmentExperienceForm.markAsPristine();
    this.showAssignmentExperienceForm = false;
    this.isEditAssignmentExperience = false;
    this.assignmentExperienceForm.reset();
  }

  openEmploymentModal(row: any): void {
    this.isEditEmployment = true;
    this.lastEmploymentRow = row;
    this.selectedEmploymentRow = row ? { ...row } : null;
    console.log('openEmploymentModal row:', row, 'employment_experience_id:', row?.employment_experience_id);
     if (row) {
      const stateKey = this.keyByValue(this.statesList, row?.state);
      const from = this.formatDateForInput(row?.from || row?.from_date_of_service || row?.from_date || row?.start_date);
      const to = this.formatDateForInput(row?.to || row?.to_date_of_service || row?.to_date || row?.end_date);
      this.employmentForm.patchValue({
        employer_name: row?.employer_name,
        from: from,
        to: to,
        city: row?.city,
        state: stateKey,
        zip_code: row?.zip_code
      }, { emitEvent: false });
    }
    this.dialogRef = this.modalService.open(this.employmentEditModal, { centered: true, size: 'xl', backdrop: 'static', windowClass: 'custom-edit-record-modal' });
    this.dialogRef.result.finally(() => {
      this.isEditEmployment = false;
      this.employmentForm.reset();
      // this.lastEmploymentRow = null;
    });
  }

  saveEmployment(): void {
    if (this.employmentForm.invalid) {
      this.employmentForm.markAllAsTouched();
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    if (this.isEmploymentDuplicate()) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }

    const payload = {
      ...this.employmentForm.value,
      person_id: this.personId,
      updated_by: this.getCurrentUserId(),
      createdDate: new Date().toISOString()
    };

    this.httpService.post(API_ENDPOINTS.USER_EXPERIENCE_TAB.Insert_Employment, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showEmploymentForm = false;
          this.employmentForm.reset();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0005'] || 'Added successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to save, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to save, Please try after sometime!')
    });
  }

  updateEmployment(): void {
    if (this.employmentForm.invalid) {
      this.employmentForm.markAllAsTouched();
      this.alertService.error(this.errorMessages['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }
    const currentEmpId = this.selectedEmploymentRow?.employment_experience_id;
    const fvEmp = this.employmentForm.value;
    const isDuplicateEmp = this.employmentRows.some((row: any) =>
      String(row['employment_experience_id']) !== String(currentEmpId) &&
      this.normalizeStr(row['employer_name']) === this.normalizeStr(fvEmp.employer_name) &&
      this.normalizeStr(this.formatDateForInput(row['from'] || row['from_date_of_service'] || row['from_date'] || row['start_date'])) === this.normalizeStr(fvEmp.from) &&
      this.normalizeStr(this.formatDateForInput(row['to'] || row['to_date_of_service'] || row['to_date'] || row['end_date'])) === this.normalizeStr(fvEmp.to) &&
      this.normalizeStr(row['city']) === this.normalizeStr(fvEmp.city) &&
      this.normalizeStr(this.keyByValue(this.statesList, row['state'])) === this.normalizeStr(fvEmp.state) &&
      this.normalizeStr(row['zip_code']) === this.normalizeStr(fvEmp.zip_code)
    );
    if (isDuplicateEmp) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    const formValue = { ...this.employmentForm.value };
    // Convert state value to key (like teaching experience)
    if (formValue.state) {
      formValue.state = this.keyByValue(this.statesList, formValue.state);
    }

    // For update, include person_id and employment_experience_id
    const payload = {
      ...formValue,
      person_id: this.personId,
      employment_experience_id: this.selectedEmploymentRow?.employment_experience_id,
      updated_by: this.getCurrentUserId()
    };
    console.log('updateEmployment payload:', payload, 'selectedEmploymentRow:', this.selectedEmploymentRow);

    this.httpService.put(API_ENDPOINTS.USER_EXPERIENCE_TAB.Update_Employment, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.showEmploymentForm = false;
          this.isEditEmployment = false;
          this.dialogRef?.close();
          this.refreshExperienceData();
          this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
          this.cd.detectChanges();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to update, Please try after sometime!');
        }
      },
      error: (err: any) => this.alertService.error(err?.error?.message || err?.message || 'Failed to update, Please try after sometime!')
    });
  }

  resetEmploymentForm(): void {
  const row =this.selectedEmploymentRow ? this.selectedEmploymentRow : null;
    if (row) {
      const stateKey = this.keyByValue(this.statesList, row?.state);
      const from = this.formatDateForInput(row?.from || row?.from_date_of_service || row?.from_date || row?.start_date);
      const to = this.formatDateForInput(row?.to || row?.to_date_of_service || row?.to_date || row?.end_date);
      this.employmentForm.patchValue({
        employer_name: row?.employer_name,
        from: from,
        to: to,
        city: row?.city,
        state: stateKey,
        zip_code: row?.zip_code
      });
    }
  }

  cancelEmploymentForm(): void {
    this.employmentForm.markAsUntouched();
    this.employmentForm.markAsPristine();
    this.showEmploymentForm = false;
    this.isEditEmployment = false;
    if (this.isEditEmployment) {
      this.dialogRef?.close();
    }
    this.employmentForm.reset();
  }
  private formatDateForInput(date: string | null | undefined): string {
    if (!date) return '';

    let formattedDate = date
    if (date && !date.includes('/')) {
      const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) formattedDate = `${m[2]}/${m[3]}/${m[1]}`;
    }
    return formattedDate;
  }
  validateDateNotBeforeDob = (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;
    if (!val || typeof val !== 'string' || !val.includes('/')) return null;
    if (val.length < 10) return null; // skip on partial input
    const parts = val.split('/');
    if (parts.length !== 3) return null;
    const selectedDate = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
    if (isNaN(selectedDate.getTime())) return null;
    const row = this.educatorDashboardRow as any;
    const dobRaw = row?.p_person_info?.[0]?.date_of_birth;
    if (!dobRaw) return null;
    const dobParts = String(dobRaw).match(/(\d{4})-(\d{2})-(\d{2})/) ||
                     String(dobRaw).match(/(\d{2})\/(\d{2})\/(\d{4})/);
    let dob: Date;
    if (dobParts && dobRaw.includes('-')) {
      dob = new Date(Number(dobParts[1]), Number(dobParts[2]) - 1, Number(dobParts[3]));
    } else if (dobParts) {
      dob = new Date(Number(dobParts[3]), Number(dobParts[1]) - 1, Number(dobParts[2]));
    } else {
      return null;
    }
    dob.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    if (selectedDate < dob) return { beforeDob: true };
    return null;
  };

  onEmploymentZipInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    const filtered = el.value.replace(/[^0-9-]/g, '').substring(0, 10);
    el.value = filtered;
    this.employmentForm.get('zip_code')?.setValue(filtered, { emitEvent: false });
  }

  onEmploymentZipBlur(event: Event): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    const ctrl = this.employmentForm.get('zip_code');
    const raw = ctrl?.value || '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 9) {
      const formatted = digits.substring(0, 5) + '-' + digits.substring(5, 9);
      el.value = formatted;
      ctrl?.setValue(formatted, { emitEvent: false });
      ctrl?.updateValueAndValidity();
    } else {
      el.value = raw;
    }
  }

  onEmploymentZipFocus(event: Event): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    el.value = this.employmentForm.get('zip_code')?.value || '';
  }

  formatZip(zip: string): string {
    return zip || '';
  }

  private fetchSmartyForEmploymentZip(zipValue: string): void {
    const zipCtrl = this.employmentForm.get('zip_code');
    if (zipCtrl?.invalid) return;
    const cleanZip = (zipValue || '').toString().replace(/\D/g, '');
    if (cleanZip.length !== 5 && cleanZip.length !== 9) return;
    this.registrationService
      .smartyValidation({ smartyInd: false, zipcode: zipValue })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.status !== 'success' || !response?.data?.length) {
            return;
          }
          const zipData = response.data[0].zipcodes?.[0];
          if (!zipData) {
            return;
          }
          const city = zipData.default_city != null ? String(zipData.default_city).trim() : '';
          const state = zipData.state != null ? String(zipData.state).trim() : '';
          const patch: Record<string, string> = {};
          if (city) patch['city'] = city;
          if (state) {
            const stateMatch = this.statesList?.find((s: any) => s.value === state || s.key === state);
            if (stateMatch) patch['state'] = stateMatch.key;
          }
          if (Object.keys(patch).length) {
            this.employmentForm.patchValue(patch, { emitEvent: false });
            if (patch['state']) {
              this.filteredVerifiableTeachingStates = [...this.statesList];
              this.stateFilterCtrl.setValue('', { emitEvent: false });
            }
          }
        }
      });
  }

  validateDate = (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;
    if (!val) return null;
    if (typeof val !== 'string') return null;

    const parts = val.split('/');
    // skip partial input until all 3 date segments are present and year has at least 3 chars
    if (parts.length < 3 || (parts[2]?.length ?? 0) < 3) return null;

    let selectedDate: Date;

    if (val.includes('/')) {
      if (parts.length !== 3) return { invalidDate: true };

      const month = Number(parts[0]);
      const day = Number(parts[1]);
      const year = Number(parts[2]);

      if (
        month < 1 || month > 12 ||
        day < 1 || day > 31 ||
        year < 1900
      ) return { invalidDate: true };

      selectedDate = new Date(year, month - 1, day);

      if (
        selectedDate.getFullYear() !== year ||
        selectedDate.getMonth() !== (month - 1) ||
        selectedDate.getDate() !== day
      ) {
        return { invalidDate: true };
      }

    } else {
      return { invalidDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(selectedDate);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate.getTime() > today.getTime()) {
      return { futureDate: true };
    }

    return null;
  };
  openOutOfStateLicenseDeleteModal(row: any): void {
    this.outOfStateLicenseDeleteRow = row;
    this.openDeleteConfirmation('out-of-state license record', () => this.confirmDeleteOutOfStateLicense());
  }

  confirmDeleteOutOfStateLicense(): void {
    const row = this.outOfStateLicenseDeleteRow;
    if (!row?.profile_out_of_state_license_id || !this.personId) {
      this.alertService.error('Missing profile_out_of_state_license_id or person_id.');
      this.outOfStateLicenseDeleteRow = null;
      return;
    }
    const payload = {
      profile_out_of_state_license_id: row.profile_out_of_state_license_id,
      person_id: this.personId
    };
    this.httpService.delete(API_ENDPOINTS.USER_EXPERIENCE_TAB.Delete_Out_of_state, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.alertService.success(this.toastMessage['T0040'] || 'Deleted successfully.');
          this.refreshExperienceData();
          this.cd.detectChanges();
          this.outOfStateLicenseDeleteRow = null;
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to delete out-of-state license. Please try again.');
          this.outOfStateLicenseDeleteRow = null;
        }
      },
      error: (err: any) => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete out-of-state license. Please try again.');
        this.outOfStateLicenseDeleteRow = null;
      }
    });
  }

  // Employment Experience Delete Modal Methods
  openEmploymentDeleteModal(row: any): void {
    this.employmentDeleteRow = row;
    this.openDeleteConfirmation('employment experience record', () => this.confirmDeleteEmployment());
  }

  confirmDeleteEmployment(): void {
    const row = this.employmentDeleteRow;
    if (!row?.employment_experience_id || !this.personId) {
      this.alertService.error('Missing employment_experience_id or person_id.');
      this.employmentDeleteRow = null;
      return;
    }
    const payload = {
      employment_experience_id: row.employment_experience_id,
      person_id: this.personId
    };
    this.httpService.patch(API_ENDPOINTS.USER_EXPERIENCE_TAB.Delete_Employment, payload).subscribe({
      next: (res: any) => {
        const success = res?.status === 'success' || res?.data?.success === true;
        if (success) {
          this.alertService.success(this.toastMessage['T0040'] || 'Deleted successfully.');
          this.refreshExperienceData();
          this.cd.detectChanges();
          this.employmentDeleteRow = null;
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to delete employment experience. Please try again.');
          this.employmentDeleteRow = null;
        }
      },
      error: (err: any) => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete employment experience. Please try again.');
        this.employmentDeleteRow = null;
      }
    });
  }

  private openDeleteConfirmation(sectionName: string, onConfirm: () => void): void {
    const options: NgbModalOptions = { backdrop: 'static', size: 'md', centered: true, windowClass: 'custom-delete-record-modal' };
    const modalRef = this.modalService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Record',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this record? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        onConfirm();
      }
    });
  }

  canShowDeleteExperienceDocument(row: any): boolean {
    return true;
  }

  confirmDeleteDocument(row: any): void {
    if (false) {
      this.alertService.warn('You are not permitted to delete this document.');
      return;
    }
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md', windowClass: 'custom-delete-record-modal' };
    const modalRef = this.modalService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Document?',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this document? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_DELETE_DOCUMENT, { document_id: row.document_id })
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res?.success === false) {
                this.alertService.error(res.message || 'Failed to delete document.');
                return;
              }
              this.alertService.success(this.toastMessage['T0040'] || 'Document deleted successfully.');
              this.refreshExperienceData();
            },
            error: () => {
              this.alertService.error('Failed to delete document. Please try again.');
            }
          });
      }
    });
  }

  private refreshExperienceData(): void {
    this.requestDashboardRefresh.emit();
  }

  viewSupportingDocument(row: any): void {
    const id = Number(row?.document_id ?? row?.profile_document_upload_id ?? row?.documentId);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, {
        profile_document_upload_id: id
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = resolveEducatorDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to retrieve document URL');
            return;
          }
          this.documentPreviewModal.openFromUrl(fileUrl, row.file_name);
        },
        error: () => {
          this.alertService.error('Failed to view document. Please try again.');
        }
      });
  }

  downloadSupportingDocument(row: any): void {
    const id = Number(row?.document_id ?? row?.profile_document_upload_id ?? row?.documentId);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, {
        profile_document_upload_id: id
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = this.resolveSupportingDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to download document');
            return;
          }
          fetch(fileUrl)
            .then(res => res.blob())
            .then(blob => {
              const fileName = String(row.file_name || `document_${id}`);
              FileSaver.saveAs(blob, fileName);
              //this.alertService.success('Document downloaded successfully.');
            })
            .catch(() => {
              this.alertService.error('Failed to download document file');
            });
        },
        error: () => {
          this.alertService.error('Failed to download document. Please try again.');
        }
      });
  }

  private resolveSupportingDocumentFileUrl(response: any): string | null {
    return (
      response?.data?.data?.p_document_blob?.file_url ||
      response?.data?.data?.p_document_blob?.fileUrl ||
      response?.data?.data?.p_document_blob?.strfileHash ||
      response?.data?.data?.p_document_blob?.file_hash ||
      response?.data?.data?.p_document_blob?.fileHash ||
      response?.data?.file_url ||
      response?.data?.fileUrl ||
      response?.data?.url ||
      response?.file_url ||
      response?.fileUrl ||
      null
    );
  }

  hasValidDocumentId(row: any): boolean {
    const value = row?.['document_id'] ?? row?.['profile_document_upload_id'] ?? row?.['documentId'];
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0;
  }

getSelectLabel(
  key: any,
  options: { key: any; value: string }[]
): string {
  if (!key || !options?.length) return '';

  const found = options.find(o => String(o.key) === String(key));
  if (!found?.value) return '';

  const trimmedLabel = found.value.trim();
  const wordCount = trimmedLabel.split(/\s+/).length;
  const charCount = trimmedLabel.length;

  return (charCount > 21) ? found.value : '';
}


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
